/* eslint-disable @typescript-eslint/no-explicit-any */

import moment from 'moment';
import {
  DefaultValue,
  SearchFacetsConstants,
  STATUS_AVAILABLE,
  P17_CODE,
  STRIKE_PRICE_THRESHOLD_PERCENTAGE,
  hardCodedAllLocale,
  searchFilters,
  showTaxesandFeesLabel,
  showAvailabilityLabel,
  dataLayerLabels,
  SPECIAL_RATES_CODE,
  STANDARD_RATES_CODE,
  searchFallbackWarningCodes,
  STATUS_NOT_AVAILABLE,
  UXL_PARTIAL_FAIL,
  WARN_CODE_1001,
  WARN_CODE_1000,
  WARN_SEARCH_1002,
  WARN_SEARCH_1009,
  UXL_COMPLETE_FAIL,
} from '../index.constant';
import {
  EEO_MEMBER_CORP_CODE,
  EEO_MEMBER_CODE,
  keyMappings,
  STATUS_MAX_OCCUPANCY_EXCEEDED,
  HQV_SESSION_ID_FAILURE,
} from '../../../constants/lib/constants';
import { SearchResultAlertType } from '../index.types';
import {
  SearchRateAmountType,
  SearchStateStoreDataType,
  SearchRateType,
  SearchStateExtendsType,
} from '../store/store.type';
import {
  SearchFacetsList,
  filterList,
  GraphQlMgpAPiResponseKey,
  DefaultCurrency,
  DefaultEURCurrency,
} from '../index.constant';
import Cookies from 'js-cookie';
import Axios from 'axios';
import {
  addSubDirectoryPrefix,
  changeCountryCodeForChina,
  formatNumberByLocale,
  getLanguage,
  logger,
  updateSessionForSpecialRates,
  fetchSessionData,
} from '../../../utils';
import { DATALAYER_CONSTANT } from '../../../constants';
import { KeysRequest, rangeFilterValType } from '../../../utils/src/dataLayer/index.types';
import { SearchRateMonetaryAmountType } from '../store/store.type';
import config from './config';
import { MILESTOMETERS, REWARDS_REDEMPTION_CLUSTER_CODES } from './application.constants';
import {
  convertArabicToEnglishNumbers,
  convertHindiToEnglishNumbers,
} from '../../../lib/organisms/SearchForm/utils/DateUtils';
import { createDecimalValue, getExchangeConversionRate, warningUtils } from '../../../modules/utils';
import { mtGC } from '@marriott/mi-headless-utils';
import { EEO_TYPE, NO_RATES_ARE_AVAILABLE, TOO_MANY_PROPERTIES } from '../../../modules/constants';
import { getDTTShopResult } from './shop.dtt.api';
import { usePageContextStore } from '../../../utils';
import { useClientEnvVarsStore } from '@marriott/mi-store-utils';

const { log } = logger({})('SearchResults/helper');
const { NEXT_PUBLIC_DEFAULT_LANG } = process.env;

declare global {
  interface Window {
    dataLayer?: Record<string, unknown>;
  }
}

export function getSearchQueryDataFromSession(data: any, sessionToken: string, currentLocale: string): any {
  const { AriesSearch, AriesCommon } = data || {};

  if (!AriesSearch) {
    return null;
  }
  const { searchCriteria, search_keyword } = AriesSearch;
  const { availabilityRequestVO, destinationAddressPlaceId, address, selectedCurrency } = searchCriteria;
  const { longitude, latitude } = address || {};
  const { checkInDate, checkOutDate, rewardsRedemption, childrenAges, numGuestsPerRoom, numRooms } =
    availabilityRequestVO;
  const checkIn =
    currentLocale === hardCodedAllLocale.ar
      ? convertArabicToEnglishNumbers(moment.utc(checkInDate).format('YYYY-MM-DD'))
      : currentLocale === hardCodedAllLocale.hi
      ? convertHindiToEnglishNumbers(moment.utc(checkInDate).format('YYYY-MM-DD'))
      : moment.utc(checkInDate).format('YYYY-MM-DD');
  const checkOut =
    currentLocale === hardCodedAllLocale.ar
      ? convertArabicToEnglishNumbers(moment.utc(checkOutDate).format('YYYY-MM-DD'))
      : currentLocale === hardCodedAllLocale.hi
      ? convertHindiToEnglishNumbers(moment.utc(checkOutDate).format('YYYY-MM-DD'))
      : moment.utc(checkOutDate).format('YYYY-MM-DD');
  const amenitiesObj: Record<string, string> = {};
  AriesSearch?.propertyFilterCriteria?.facets?.amenities?.forEach((amentiy: string) => {
    amenitiesObj[amentiy] = amentiy;
  });
  const activitiesObj: Record<string, string> = {};
  AriesSearch?.propertyFilterCriteria?.facets?.['activities-on-site']?.forEach((activity: string) => {
    activitiesObj[activity] = activity;
  });
  const brandsObj: Record<string, string> = {};
  AriesSearch?.propertyFilterCriteria?.facets?.brands?.forEach((brand: string) => {
    brandsObj[brand] = brand;
  });
  const transportationTypesObj: Record<string, string> = {};
  AriesSearch?.propertyFilterCriteria?.facets?.['transportation-types']?.forEach((transportationType: string) => {
    transportationTypesObj[transportationType] = transportationType;
  });
  const hotelTypesObj: Record<string, string> = {};
  AriesSearch?.propertyFilterCriteria?.facets?.['property-types']?.forEach((hotelType: string) => {
    hotelTypesObj[hotelType] = hotelType;
  });
  const eventsObj: Record<string, string> = {};
  AriesSearch?.propertyFilterCriteria?.facets?.['meetings-and-events']?.forEach((event: string) => {
    eventsObj[event] = event;
  });
  const statesObj: Record<string, string> = {};
  AriesSearch?.propertyFilterCriteria?.facets?.states?.forEach((state: string) => {
    statesObj[state] = state;
  });
  const citiesObj: Record<string, string> = {};
  AriesSearch?.propertyFilterCriteria?.facets?.cities?.forEach((city: string) => {
    citiesObj[city] = city;
  });

  const pointsObj: Record<string, number> =
    AriesSearch?.propertyFilterCriteria?.rangeFilters &&
    AriesSearch?.propertyFilterCriteria?.rangeFilters?.points !== undefined
      ? AriesSearch?.propertyFilterCriteria?.rangeFilters?.points[0] !== '0'
        ? AriesSearch?.propertyFilterCriteria?.rangeFilters?.points?.reduce(
            (acc: rangeFilterValType, points: number) => ({ ...acc, [points]: Number(points) }),
            {}
          )
        : {}
      : {};

  const distanceObj: Record<string, number> =
    AriesSearch?.propertyFilterCriteria?.rangeFilters &&
    AriesSearch?.propertyFilterCriteria?.rangeFilters?.distance !== undefined
      ? AriesSearch?.propertyFilterCriteria?.rangeFilters?.distance[0] !== '0'
        ? AriesSearch?.propertyFilterCriteria?.rangeFilters?.distance?.reduce(
            (acc: rangeFilterValType, distance: number) => ({ ...acc, [distance]: Number(distance) }),
            {}
          )
        : {}
      : {};

  const priceObj: Record<string, number> =
    AriesSearch?.propertyFilterCriteria?.rangeFilters &&
    AriesSearch?.propertyFilterCriteria?.rangeFilters?.price !== undefined
      ? AriesSearch?.propertyFilterCriteria?.rangeFilters?.price[0] !== '0'
        ? AriesSearch?.propertyFilterCriteria?.rangeFilters?.price?.reduce(
            (acc: rangeFilterValType, price: number) => ({ ...acc, [price]: Number(price) }),
            {}
          )
        : {}
      : {};

  let specialRateCode = availabilityRequestVO?.clusterCode;
  if (
    specialRateCode === 'AAA' ||
    specialRateCode === 'GOV' ||
    specialRateCode === 'S9R' ||
    specialRateCode === 'CORP'
  ) {
    specialRateCode = specialRateCode.toLocaleLowerCase();
  }

  // FOR EEO enabled users
  const EEOMemberLevel =
    AriesCommon?.memState === 'authenticated' && data.memberLevel
      ? EEO_MEMBER_CORP_CODE[data.memberLevel]
      : AriesCommon?.memState === 'remembered' && AriesSearch.rememberedMemberLevel
      ? EEO_MEMBER_CORP_CODE[AriesSearch.rememberedMemberLevel]
      : null;

  /*
    Read the special rates value from session data,
    and populate rateRequestTypes for UXL calls
  */
  let ratesType: Array<{ type: string | undefined; value: string | undefined } | []> = [];
  switch (specialRateCode) {
    case 's9r':
      ratesType = [
        {
          type: 'CLUSTER',
          value: specialRateCode,
        },
      ];
      break;
    case 'corp':
      // Check if it is Rewards Redemption flow based on the cluster code that is entered
      if (REWARDS_REDEMPTION_CLUSTER_CODES.includes(availabilityRequestVO?.corporateCode?.toLocaleUpperCase())) {
        ratesType = [
          {
            type: 'CLUSTER',
            value: 'MRW',
          },
          {
            type: 'CLUSTER',
            value: 'P17',
          },
          {
            type: 'STANDARD',
            value: '',
          },
        ];
      } else {
        ratesType = [
          {
            type: 'CLUSTER',
            value: availabilityRequestVO?.corporateCode?.trim(),
          },
        ];
      }
      break;
    case 'gov':
      ratesType = [
        {
          type: 'GOV',
          value: specialRateCode,
        },
      ];
      break;
    case 'aaa':
      ratesType = [
        {
          type: 'AAA',
          value: specialRateCode,
        },
      ];
      break;
    default:
      ratesType = [
        {
          type: 'STANDARD',
          value: '',
        },
      ];
      break;
  }
  // for strikethorugh story, EEO members may get different rates based on there level.
  if (EEOMemberLevel && availabilityRequestVO?.clusterCode === 'none') {
    ratesType = [
      {
        type: 'STANDARD',
        value: '',
      },
      {
        type: 'CLUSTER',
        value: EEOMemberLevel,
      },
    ];
  }
  if (rewardsRedemption) {
    ratesType = [
      {
        type: 'CLUSTER',
        value: 'MRW', // hardcode the value to get the points on the search result
      },
      {
        type: 'STANDARD',
        value: '',
      },
      {
        type: 'CLUSTER',
        value: 'P17',
      },
    ];
  }
  /*
  retrieving domain and using it to enable mandatory fee
  */
  const isMandatoryFeeApplicable = config?.ALLOWED_DOMAINS.includes(currentLocale);
  const defaultObj = {
    sessionToken,
    keyword: search_keyword,
    latitude: latitude ?? null,
    longitude: longitude ?? null,
    destinationAddressPlaceId: destinationAddressPlaceId,
    amenities: amenitiesObj,
    usePoints: rewardsRedemption,
    activities: activitiesObj,
    brands: brandsObj,
    transportationTypes: transportationTypesObj,
    hotelTypes: hotelTypesObj,
    events: eventsObj,
    states: statesObj,
    cities: citiesObj,
    showFullPrice: searchCriteria?.showFullPrice === 'true',
    showAvailableHotels: isUnsuccessfulUserJourney() ? false : searchCriteria?.showAvailableHotels,
    selectedCurrency: selectedCurrency,
    points: pointsObj,
    distance: distanceObj,
    price: priceObj,
    options: {
      startDate: checkIn,
      endDate: checkOut,
      includeMandatoryFees: isMandatoryFeeApplicable ? true : false,
      numberInParty: numGuestsPerRoom,
      childAges: childrenAges?.split(',')?.map((key: any) => {
        return parseInt(key);
      }),
      rateRequestTypes: ratesType,
      quantity: numRooms,
    },
  };
  return defaultObj;
}

export function getPriceCommaSeparator(
  numb: number,
  searchQueryState: SearchStateStoreDataType,
  currentLocale: string,
  hqvCurrency: string = ''
): string {
  let str: string | number = numb;
  const currentCurrencyValue = hqvCurrency ? hqvCurrency : searchQueryState.currentCurrency;
  if (currentCurrencyValue !== DefaultValue) {
    //replaced toFixed with .floor to have exact price as HQV and Aries
    str = Math.floor(numb);
  }
  return formatNumberByLocale(str, currentLocale);
}

export function getPriceByDecimalPoint(
  price: any,
  decimalPoint: number | undefined,
  searchQueryState: SearchStateStoreDataType,
  currentLocale: string,
  currentPropertyCurrency: string | undefined,
  hqvConversionRate: any = null,
  hqvCurrency: string = ''
): string {
  const defaultCurrency = currentLocale === hardCodedAllLocale.de ? DefaultEURCurrency : DefaultCurrency;
  const currentCurrency = currentPropertyCurrency ?? defaultCurrency;
  const currPrice = createDecimalValue(price, decimalPoint as number);

  const conversionRateArray = hqvConversionRate ? hqvConversionRate : searchQueryState.currentConversionRate;
  const currentConverSionRateArr = conversionRateArray?.filter(
    (key: { currency: string }) => key.currency === currentCurrency
  );
  const currentConverSionRate = currentConverSionRateArr[0]?.currentConversionRate || 1; // set 1 to avoid null value
  const currentCurrencyValue = hqvCurrency ? hqvCurrency : searchQueryState.currentCurrency;
  const convertedPrice =
    currentCurrencyValue === DefaultValue
      ? Math.floor(currPrice)
      : getExchangeConversionRate(currPrice, currentConverSionRate);
  return getPriceCommaSeparator(convertedPrice, searchQueryState, currentLocale, hqvCurrency);
}

export function getCurrRateAmountBlock(block: SearchRateType | null): SearchRateAmountType | null | undefined {
  // return rate amount block that to be used on search result
  if (!block) {
    return null;
  }
  return { ...block.rateModes?.lowestAverageRate, status: block?.status };
}

/**
 * Fetches the appropriate price rate block from the GraphQL response based on rate category and user selection.
 * Prioritizes: 1) Selected special rate, 2) ACRS-sourced rate (when rate types selected), 3) Standard rate, 4) EEO fallback.
 *
 * @param currState - Current search state containing rate request types
 * @param rateBlockArr - Array of rate blocks from the API
 * @returns The rate block to display for pricing, or null
 */
export function getCurrentPriceRateBlock(
  currState: SearchStateStoreDataType,
  rateBlockArr?: Array<SearchRateType>
): SearchRateType | null {
  if (!rateBlockArr) {
    return null;
  }
  const rateTypeArr = currState[SearchFacetsConstants.search]?.[SearchFacetsConstants.options]?.rateRequestTypes;
  let standardRateBlock: SearchRateType | null = {};
  let specialRateBlock;
  // Iterate through rate blocks to identify standard and special rate candidates
  rateBlockArr?.forEach((rateBlock: SearchRateType) => {
    // Standard rate: rateCategory.value is null and code matches STANDARD_RATES_CODE
    if (rateBlock.rateCategory?.value === null && rateBlock.rateCategory?.code === STANDARD_RATES_CODE) {
      standardRateBlock = rateBlock;
    }
    // Special rate: rate category value matches the single selected rate request type
    else if (
      rateTypeArr?.length &&
      rateTypeArr?.length === 1 &&
      rateBlock.rateCategory?.value?.toLowerCase() === rateTypeArr[0]?.value?.toString().toLowerCase()
    ) {
      specialRateBlock = rateBlock;
    }
    // WEB-184546: ACRS rates - When rate comes from ACRS (Advanced Corporate Rate Search) and user has
    // selected rate request types, treat it as the special rate to display
    else if (
      rateBlock.sourceOfRate === 'ACRS' &&
      rateTypeArr?.length &&
      rateBlock?.rateCategory?.value !== 'MRW' &&
      rateBlock?.rateCategory?.value !== 'P17'
    ) {
      specialRateBlock = rateBlock;
    }
  });
  if (specialRateBlock) {
    return specialRateBlock;
  }
  // If standard rates are missing or invalid, use EEO rates as fallback on UI
  if (
    !Object.keys(standardRateBlock).length ||
    (!standardRateBlock.rateModes && standardRateBlock?.status?.code !== STATUS_MAX_OCCUPANCY_EXCEEDED)
  ) {
    const rateBlock = rateBlockArr?.filter(
      (key: SearchRateType) =>
        key.rateCategory?.value &&
        EEO_MEMBER_CODE.indexOf(key.rateCategory?.value) > -1 &&
        key.rateCategory?.code === SPECIAL_RATES_CODE
    );
    standardRateBlock = getRateBlock(rateBlock, EEO_TYPE);
  }
  return standardRateBlock;
}

// fetch rate block from graphql object on the basis of points savers flag
export function getCurrentPointSaversRateBlock(rateBlockArr?: Array<SearchRateType>): SearchRateType | null {
  if (!rateBlockArr) {
    return null;
  }
  const pointSaversBlock = rateBlockArr?.filter((key: SearchRateType) => {
    if (key.rateCategory?.value === P17_CODE && key.status?.code === STATUS_AVAILABLE) {
      return true;
    } else {
      return false;
    }
  })[0];
  return pointSaversBlock;
}

//return points dynamically based on availablity of point savers
const getAvailablePoints = (rateBlockArr: Array<SearchRateType>) => {
  const p17Block = rateBlockArr?.filter(
    (key: SearchRateType) =>
      key?.rateCategory?.code === SPECIAL_RATES_CODE &&
      key?.rateCategory?.value === P17_CODE &&
      (key?.rateModes?.pointsPerUnit?.points || key?.status?.code === STATUS_MAX_OCCUPANCY_EXCEEDED)
  )[0];
  if (p17Block) {
    return p17Block;
  } else {
    const availablePointsBlock = rateBlockArr?.filter(
      (key: SearchRateType) =>
        key?.rateCategory?.code === SPECIAL_RATES_CODE &&
        key?.rateCategory?.value !== P17_CODE &&
        (key?.rateModes?.pointsPerUnit?.points || key?.status?.code === STATUS_MAX_OCCUPANCY_EXCEEDED)
    )[0];
    return availablePointsBlock;
  }
};

// fetch points block from graphql object on the basis of rate category
export function getCurrentRatePointsBlock(
  rateBlockArr?: Array<SearchRateType>
): SearchRateAmountType | null | undefined {
  if (!rateBlockArr) {
    return null;
  }
  const rateBlock = getAvailablePoints(rateBlockArr);
  return rateBlock?.rateModes?.pointsPerUnit;
}

export function getRateBlock(rateBlockArr?: Array<SearchRateType>, filterType?: string): SearchRateType | null {
  if (!rateBlockArr) {
    return null;
  }
  let rateBlock = {};
  if (filterType === 'points') {
    rateBlock = getAvailablePoints(rateBlockArr);
  } else if (filterType === EEO_TYPE) {
    rateBlock = rateBlockArr?.filter(
      (key: SearchRateType) =>
        key.rateCategory?.value &&
        EEO_MEMBER_CODE.indexOf(key.rateCategory?.value) > -1 &&
        key.rateCategory?.code === SPECIAL_RATES_CODE
    )[0];
  }
  return rateBlock;
}

export function getSpecialRateBlock(rateBlockArr: Array<SearchRateType>, clusterCode: string) {
  const selectedRateBlock = rateBlockArr?.filter(
    (rateBlock: SearchRateType) =>
      rateBlock.rateCategory?.code === SPECIAL_RATES_CODE && rateBlock.rateCategory?.value === clusterCode
  )[0];
  return selectedRateBlock;
}

export function getAlertsData(alert: any, type: string): SearchResultAlertType {
  let info = {};
  switch (type) {
    case 'info':
      info = {
        code: '',
        heading: alert?.alertHeadingLabel ? alert.alertHeadingLabel : '',
        hasLink: false,
        linkUrl: '',
        linkText: alert?.alertLinkTextLabel ? alert.alertLinkTextLabel : '',
        errorMessage: alert?.alertMessageLabel ? alert.alertMessageLabel : '',
        showPageAndFilter: false,
        icon: 'icon-information',
        type: 'info',
      };
      break;
    case 'warning':
      info = {
        code: 'queries.v2.properties.limit.range.dev.2',
        heading: alert?.alertHeadingLabel ? alert.alertHeadingLabel : '',
        hasLink: false,
        linkUrl: '',
        linkText: alert?.alertLinkTextLabel ? alert.alertLinkTextLabel : '',
        errorMessage: alert?.alertMessageLabel ? alert.alertMessageLabel : '',
        showPageAndFilter: true,
        icon: 'icon-warning',
        type: 'warning',
      };
      break;
    case 'success':
      info = {
        code: '',
        heading: alert?.alertHeadingLabel ? alert.alertHeadingLabel : '',
        hasLink: false,
        linkUrl: '',
        // linkCallbackFunc: setDefaultPrice(),
        linkText: alert?.alertLinkTextLabel ? alert.alertLinkTextLabel : '',
        errorMessage: alert?.alertMessageLabel ? alert.alertMessageLabel : '',
        showPageAndFilter: true,
        icon: 'icon-success',
        type: 'success',
      };
      break;
    //error message shows any of the below case satisfies
    case 'search.form.near.address.property.result.not.found':
      info = {
        code: 'search.form.near.address.property.result.not.found',
        heading: alert?.alertHeadingLabel ? alert.alertHeadingLabel : '',
        hasLink: false,
        linkUrl: '',
        linkText: alert?.alertLinkTextLabel ? alert.alertLinkTextLabel : '',
        errorMessage: alert?.alertMessageLabel ? alert.alertMessageLabel : '',
        showPageAndFilter: true,
        icon: 'icon-information',
        type: 'search-form-near',
      };
      break;
    case 'no.hotels.within.radius.error.msg':
    case 'WARN-SEARCH-1002':
      info = {
        code: 'no.hotels.within.radius.error.msg',
        heading: alert?.alertHeadingLabel ? alert.alertHeadingLabel : '',
        hasLink: true,
        linkUrl: '',
        icon: 'icon-information',
        linkText: alert?.alertLinkTextLabel ? alert.alertLinkTextLabel : '',
        errorMessage: alert?.alertMessageLabel ? alert.alertMessageLabel : '',
        showPageAndFilter: true,
        type: 'no-hotels',
      };
      break;
    case 'no.availability.within.radius.error.msg':
    case 'AMP181':
    case 'WARN-RATES-1000':
      info = {
        code: type === 'WARN-RATES-1000' ? type : 'no.availability.within.radius.error.msg',
        heading: alert?.alertHeadingLabel ? alert.alertHeadingLabel : '',
        hasLink: true,
        linkUrl: '',
        linkText: alert?.alertLinkTextLabel ? alert.alertLinkTextLabel : '',
        errorMessage: alert?.alertMessageLabel ? alert.alertMessageLabel : '',
        showPageAndFilter: true,
        icon: 'icon-information',
        type: 'no-availability',
      };
      break;
    //LAR-1003 case
    case 'property.search.hotels.maxcapacity.not.available':
      info = {
        code: 'property.search.hotels.maxcapacity.not.available',
        heading: alert?.alertHeadingLabel ? alert.alertHeadingLabel : '',
        hasLink: false,
        linkUrl: '',
        linkText: alert?.alertLinkTextLabel ? alert.alertLinkTextLabel : '',
        errorMessage: alert?.alertMessageLabel ? alert.alertMessageLabel : '',
        showPageAndFilter: true,
        icon: 'icon-information',
        type: 'property-search',
      };
      break;
    case 'search.form.property.result.not.found':
      info = {
        code: '',
        heading: alert?.alertHeadingLabel ? alert.alertHeadingLabel : '',
        hasLink: false,
        linkUrl: '',
        linkText: alert?.alertLinkTextLabel ? alert.alertLinkTextLabel : '',
        errorMessage: alert?.alertMessageLabel ? alert.alertMessageLabel : '',
        showPageAndFilter: true,
        icon: 'icon-information',
        type: 'result-not-found',
      };
      break;
    //UnsuccessfulSell alert
    case 'UnsuccessfulSell':
      info = {
        code: '',
        heading: alert?.alertHeadingLabel ? alert.alertHeadingLabel : '',
        hasLink: false,
        linkUrl: '',
        linkText: alert?.alertLinkTextLabel ? alert.alertLinkTextLabel : '',
        errorMessage: alert?.alertMessageLabel ? alert.alertMessageLabel : '',
        showPageAndFilter: true,
        icon: 'icon-warning',
        type: 'unsuccessfulSell',
      };
      break;
    case 'queries.v2.properties.limit.range.dev.2':
      info = {
        code: 'queries.v2.properties.limit.range.dev.2',
        heading: alert?.alertHeadingLabel ? alert.alertHeadingLabel : '',
        hasLink: false,
        linkUrl: '',
        icon: 'icon-information',
        linkText: alert?.alertLinkTextLabel ? alert.alertLinkTextLabel : '',
        errorMessage: alert?.alertMessageLabel ? alert.alertMessageLabel : '',
        showPageAndFilter: true,
        type: 'warning',
      };
      break;
    case 'WARN-RATES-1001':
      info = {
        code: '',
        heading: alert?.alertHeadingLabel ? alert.alertHeadingLabel : '',
        hasLink: false,
        linkUrl: '',
        linkText: alert?.alertLinkTextLabel ? alert.alertLinkTextLabel : '',
        errorMessage: alert?.alertMessageLabel ? alert.alertMessageLabel : '',
        showPageAndFilter: true,
        icon: 'icon-warning',
        type: 'warning',
      };
      break;
    default:
      info = {
        code: '',
        heading: '',
        hasLink: false,
        linkUrl: '',
        linkText: '',
        errorMessage: '',
        showPageAndFilter: true,
        icon: '',
        // type:''
      };
      break;
  }
  return info;
}

export function getTotalFilterCount(obj: SearchStateStoreDataType | SearchStateExtendsType | null): number {
  const arr = Object.values(SearchFacetsList)?.map((filterType, _filterKey) => {
    const tempSearchData = obj?.[filterType] ?? {};
    return Object.keys(tempSearchData).length;
  });
  return arr.reduce((prevVal, val) => prevVal + val);
}

/**
 * Recursively sorts object keys for consistent JSON stringification.
 * Ensures that objects with the same keys/values but different order
 * produce identical strings when stringified.
 */
function sortObjectKeys(obj: unknown): unknown {
  if (obj === null || typeof obj !== 'object') {
    return obj;
  }
  if (Array.isArray(obj)) {
    return obj.map(sortObjectKeys);
  }
  return Object.keys(obj as Record<string, unknown>)
    .sort()
    .reduce((sorted, key) => {
      sorted[key] = sortObjectKeys((obj as Record<string, unknown>)[key]);
      return sorted;
    }, {} as Record<string, unknown>);
}

/**
 * Compares two filter state objects to determine if they are equal.
 * Used to prevent unnecessary UXL/ESP calls when Apply is clicked without filter changes.
 */
export function isFilterStateEqual(prevState: SearchStateExtendsType | null | undefined, newState: any): boolean {
  if (!prevState && !newState) return true;
  if (!prevState || !newState) return false;

  const filterKeys = Object.values(SearchFacetsList);

  for (const key of filterKeys) {
    const prevVal = prevState[key] ?? {};
    const newVal = newState[key] ?? {};

    if (JSON.stringify(sortObjectKeys(prevVal)) !== JSON.stringify(sortObjectKeys(newVal))) {
      return false;
    }
  }
  return true;
}

export function getScreenCategory() {
  let screenCategory = '';
  if (window.innerWidth < 240) {
    screenCategory = 'XXS';
  } else if (window.innerWidth >= 240 && window.innerWidth < 320) {
    screenCategory = 'XS';
  } else if (window.innerWidth >= 320 && window.innerWidth < 480) {
    screenCategory = 'S';
  } else if (window.innerWidth >= 480 && window.innerWidth < 560) {
    screenCategory = 'SM';
  } else if (window.innerWidth >= 560 && window.innerWidth < 672) {
    screenCategory = 'M';
  } else if (window.innerWidth >= 672 && window.innerWidth < 768) {
    screenCategory = 'MML';
  } else if (window.innerWidth >= 768 && window.innerWidth < 864) {
    screenCategory = 'ML';
  } else if (window.innerWidth >= 864 && window.innerWidth < 960) {
    screenCategory = 'MLL';
  } else if (window.innerWidth >= 960 && window.innerWidth < 1024) {
    screenCategory = 'L';
  } else if (window.innerWidth >= 1024 && window.innerWidth < 1200) {
    screenCategory = 'XL';
  } else if (window.innerWidth >= 1200 && window.innerWidth < 1440) {
    screenCategory = 'XXL';
  } else if (window.innerWidth >= 1440) {
    screenCategory = 'XXXL';
  } else {
    screenCategory = 'Unknown';
  }

  return screenCategory;
}

export function updateDataLayerObj(
  dataLayer: Record<string, unknown>,
  _searchResults: any,
  _searchQuery: SearchStateStoreDataType | any,
  isFlexibleDateSearch: boolean,
  isMapViewEnabled: boolean,
  datalayerProperties: any,
  isAutoSuggestSelectedSearch?: boolean,
  isRecentViewSelectedSearch?: boolean,
  sortByOptions?: string | null,
  referer?: string,
  isIncludeUnAvailableProperties?: boolean
) {
  let deviceOrientation = datalayerProperties?.NEXT_PUBLIC_ORIENTATION_LANDSCAPE;
  const screenCategory = getScreenCategory();
  const params = new URLSearchParams(window.location.search);
  // screenCategory = window.innerWidth < 576 ? 'S' : window.innerWidth < 768 ? 'M' : window.innerWidth < 992 ? 'L' : 'XXL';
  if (window.innerWidth < 576 && window.innerHeight > window.innerWidth) {
    deviceOrientation = datalayerProperties?.NEXT_PUBLIC_ORIENTATION_PORTRAIT;
  }
  let startIndex = _searchResults?.pageInfo?.currentOffset ?? 0;
  if (_searchResults?.total) {
    startIndex = startIndex + 1;
  }
  const endIndex =
    _searchResults?.pageInfo?.hasNextPage === true ? _searchResults?.pageInfo?.nextOffset : _searchResults?.total ?? 0;
  const recordsValue = `${startIndex}*${endIndex}`;
  const totalPages = Math.ceil(parseInt(_searchResults?.total) / _searchQuery?.limit);

  let marshaListArray: any = [];
  const allRateList: any = [];
  const allCurrencyList: any = [];
  let criteria2: any = [];
  let criteriaCurrency: any = [];

  if (!isFlexibleDateSearch) {
    marshaListArray = _searchResults?.edges?.map(
      (res: { node: { property: { id: string } } }) => res?.node?.property?.id
    );

    //updated the code when points are applied.
    if (_searchQuery?.pointsEnable) {
      _searchResults?.edges?.forEach((edge: any) => {
        const transformedRatesNode = edge?.node?.rates;
        const currRateNode = getCurrentPriceRateBlock(_searchResults, transformedRatesNode);
        const currRateDetails = getCurrRateAmountBlock(currRateNode);
        if (currRateDetails) {
          const ratesToPush =
            currRateDetails?.amount?.amount &&
            (currRateDetails?.amount?.decimalPoint || currRateDetails?.amount?.decimalPoint === 0) &&
            Math.floor(createDecimalValue(currRateDetails?.amount?.amount, currRateDetails?.amount?.decimalPoint));
          const currencyToPush = currRateDetails?.amount?.currency;
          allRateList.push(ratesToPush);
          allCurrencyList.push(currencyToPush);
        } else {
          allRateList.push('NA');
          allCurrencyList.push('NA');
        }
      });
      criteria2 = allRateList;
      criteriaCurrency = allCurrencyList;
    } else {
      //updated the code for when includeMandatoryFees is true.
      if (_searchQuery?.search?.options?.includeMandatoryFees) {
        _searchResults?.edges?.forEach((edge: any) => {
          const transformedRatesNode = edge?.node?.rates;
          const currRateNode = getCurrentPriceRateBlock(_searchQuery, transformedRatesNode);
          const currRateDetails = getCurrRateAmountBlock(currRateNode);
          if (
            currRateDetails &&
            currRateDetails.amountPlusMandatoryFees &&
            currRateDetails.amountPlusMandatoryFees.amount &&
            (currRateDetails.amountPlusMandatoryFees.decimalPoint ||
              currRateDetails.amountPlusMandatoryFees.decimalPoint === 0)
          ) {
            const ratesToPush = Math.floor(
              createDecimalValue(
                currRateDetails?.amountPlusMandatoryFees?.amount,
                currRateDetails?.amountPlusMandatoryFees?.decimalPoint
              )
            );
            const currencyToPush = currRateDetails?.amountPlusMandatoryFees?.currency;
            allRateList.push(ratesToPush);
            allCurrencyList.push(currencyToPush);
          } else {
            allRateList.push('NA');
            allCurrencyList.push('NA');
          }
        });
      } else {
        //updated the code for when includeMandatoryFees is false.
        _searchResults?.edges?.forEach((edge: any) => {
          const transformedRatesNode = edge?.node?.rates;
          const currRateNode = getCurrentPriceRateBlock(_searchQuery, transformedRatesNode);
          const currRateDetails = getCurrRateAmountBlock(currRateNode);
          if (currRateDetails) {
            const ratesToPush =
              currRateDetails?.amount?.amount &&
              (currRateDetails?.amount?.decimalPoint || currRateDetails?.amount?.decimalPoint === 0) &&
              Math.floor(createDecimalValue(currRateDetails?.amount?.amount, currRateDetails?.amount?.decimalPoint));
            const currencyToPush = currRateDetails?.amount?.currency;
            allRateList.push(ratesToPush);
            allCurrencyList.push(currencyToPush);
          } else {
            allRateList.push('NA');
            allCurrencyList.push('NA');
          }
        });
      }
      criteria2 = allRateList;
      criteriaCurrency = allCurrencyList;
    }
  } else {
    marshaListArray = _searchResults?.edges?.map((res: { node: { id: string } }) => res?.node?.id);
  }

  let selectedFilters = '';
  let uniqueSelectedFilters = '';
  let appliedSearchFilters = '';
  Object.keys(_searchQuery).forEach(key => {
    filterList.forEach(filter => {
      if (key === filter && _searchQuery[filter] && Object.values(_searchQuery[filter]).length > 0) {
        const selectedFilter = Object.values(_searchQuery[filter]).toString();
        selectedFilters = selectedFilters + selectedFilter + ',';
        if (filter === dataLayerLabels.price) {
          uniqueSelectedFilters = uniqueSelectedFilters + `${selectedFilter}-PRICE,`;
          appliedSearchFilters = appliedSearchFilters + key + `:${selectedFilter}-PRICE|`;
        } else if (filter === dataLayerLabels.points) {
          uniqueSelectedFilters = uniqueSelectedFilters + `${selectedFilter}-POINT,`;
          appliedSearchFilters = appliedSearchFilters + key + `:${selectedFilter}-POINT,|`;
        } else if (filter === dataLayerLabels.distance) {
          uniqueSelectedFilters = uniqueSelectedFilters + `${selectedFilter}-DIST,`;
          appliedSearchFilters = appliedSearchFilters + key + `:${selectedFilter}-DIST|`;
        } else {
          uniqueSelectedFilters = uniqueSelectedFilters + selectedFilter + ',';
          appliedSearchFilters = appliedSearchFilters + key + ':' + selectedFilter + '|';
        }
      }
    });
  });

  const completeFilterList: { [x: string]: string }[] = [];
  _searchResults?.facets?.forEach((bucketList: any) => {
    const bucketType = bucketList?.type?.code;
    bucketList?.buckets?.forEach(
      (filter: {
        code: string;
        label: string;
        description: string;
        index: number;
        count: number;
        start: string;
        end: string;
      }) => {
        if (filter.code) {
          if (filter.code === filter.description) {
            completeFilterList.push({ [filter.code]: `${filter.label} (${filter.count})` });
          } else {
            completeFilterList.push({ [filter.code]: `${filter.description} (${filter.count})` });
          }
        } else {
          if (bucketType === 'price') {
            completeFilterList.push({ [`${filter.index}-PRICE`]: `${filter.start}-${filter.end}` });
          } else if (bucketType === 'distance') {
            completeFilterList.push({
              [`${filter.index}-DIST`]: `${Math.floor(parseInt(filter?.start) / MILESTOMETERS)?.toFixed(
                0
              )}-${Math.floor(parseInt(filter?.end) / MILESTOMETERS)?.toFixed(0)}`,
            });
          } else {
            completeFilterList.push({ [`${filter.index}-POINT`]: `${filter.start}-${filter.end}` });
          }
        }
      }
    );
  });

  let selectedFilterArr: any = [];
  selectedFilterArr = uniqueSelectedFilters.split(',');

  let selectedFilterLabels = '';
  selectedFilterArr.forEach((key: string) => {
    completeFilterList.forEach(filter => {
      if (key in filter) {
        const selectedFilterLabel = filter[key];
        selectedFilterLabels = selectedFilterLabels + selectedFilterLabel + '|';
      }
    });
  });

  if (selectedFilterLabels.charAt(selectedFilterLabels.length - 1) === '|') {
    selectedFilterLabels = selectedFilterLabels.slice(0, -1);
  }

  let appliedSearchFilterArr: any = [];
  appliedSearchFilterArr = appliedSearchFilters.split('|');

  let appliedSearchFilterLabels = '';
  appliedSearchFilterArr.forEach((key: string) => {
    completeFilterList.forEach(filter => {
      key
        .split(':')[1]
        ?.split(',')
        ?.forEach(filterKey => {
          if (filterKey in filter) {
            const filterCategory =
              key.split(':')[0].charAt(0) + key.split(':')[0].toLowerCase().slice(1).replace('_', ' ') + ':';
            const appliedFilterLabel =
              key.split(':')[0] === dataLayerLabels.points ||
              key.split(':')[0] === dataLayerLabels.price ||
              key.split(':')[0] === dataLayerLabels.brand ||
              key.split(':')[0] === dataLayerLabels.distance ||
              key.split(':')[0] === dataLayerLabels.states
                ? filterCategory + filter[filterKey].split('(')[0].trim()
                : filterCategory +
                  filterKey.split(':')[0].charAt(0).toUpperCase() +
                  filterKey.split(':')[0].toLowerCase().slice(1).replace('-', ' ');
            appliedSearchFilterLabels = appliedSearchFilterLabels + appliedFilterLabel + '|';
          }
        });
    });
  });

  if (_searchQuery?.currentCurrency) {
    appliedSearchFilterLabels =
      appliedSearchFilterLabels +
      (_searchQuery?.currentCurrency !== 'default'
        ? searchFilters.selectedCurrency.charAt(0).toUpperCase() +
          searchFilters.selectedCurrency.slice(1) +
          ':' +
          _searchQuery?.currentCurrency
        : searchFilters.selectedCurrency.charAt(0).toUpperCase() +
          searchFilters.selectedCurrency.slice(1) +
          ':' +
          (_searchQuery?.currentOriginCurrency ? _searchQuery?.currentOriginCurrency : _searchQuery?.currentCurrency)) +
      '|';
  }

  if (!_searchQuery?.includeUnavailableProperties) {
    appliedSearchFilterLabels = appliedSearchFilterLabels + showAvailabilityLabel + '|';
  } else {
    appliedSearchFilterLabels.replace(`|${showAvailabilityLabel}`, '');
  }

  if (_searchQuery?.includeTaxesAndFees) {
    appliedSearchFilterLabels = appliedSearchFilterLabels + showTaxesandFeesLabel + '|';
  } else {
    appliedSearchFilterLabels.replace(`|${showTaxesandFeesLabel}`, '');
  }

  if (appliedSearchFilterLabels.charAt(appliedSearchFilterLabels.length - 1) === '|') {
    appliedSearchFilterLabels = appliedSearchFilterLabels.slice(0, -1);
  }

  let pathType = '';
  if (Cookies.get('newSearch')) {
    pathType = datalayerProperties?.NEXT_PUBLIC_PATHTYPE_NEW ?? '';
  } else {
    pathType = datalayerProperties?.NEXT_PUBLIC_PATHTYPE_LEGACY ?? '';
  }
  dataLayer['search_show_available_hotels'] = isIncludeUnAvailableProperties ? 'FALSE' : 'TRUE';
  dataLayer['search_criteria_filter_selected_ids'] = selectedFilters.replace(/,\s*$/, '');
  dataLayer['search_result_refinements'] = selectedFilterLabels;
  dataLayer['search_filters_applied'] = appliedSearchFilterLabels;
  dataLayer['search_list_records_start_end_index'] = recordsValue;
  dataLayer['search_res_path_type'] = pathType;
  dataLayer['search_list_records_on_page'] =
    _searchResults?.total > _searchQuery?.limit ? _searchQuery?.limit?.toString() : _searchResults?.total?.toString();
  dataLayer['search_multiRate_or_single'] = datalayerProperties?.NEXT_PUBLIC_SEARCH_SINGLE_RATE ?? '';
  dataLayer['search_list_pages_total'] = totalPages?.toString();
  dataLayer['search_list_number_of_page'] = totalPages?.toString();
  dataLayer['search_list_page_current'] = parseInt(dataLayer['search_list_page_current'] as any);
  dataLayer['search_impression_criteria3'] = `${_searchQuery?.sort?.fields[0]?.field?.toLowerCase()};${
    !isFlexibleDateSearch && _searchQuery?.currentCurrency !== 'default' ? _searchQuery?.currentCurrency : ''
  };${_searchQuery?.includeUnavailableProperties ? false : true};${_searchQuery?.limit};${_searchQuery?.nextPage + 1};${
    _searchResults?.total
  }`;
  dataLayer['search_impression_criteria2'] = !isFlexibleDateSearch
    ? criteria2?.toString()?.replaceAll(',', ';').replaceAll(';;', ';')
    : '';
  dataLayer['search_impression_criteria2_currency'] = !isFlexibleDateSearch
    ? criteriaCurrency?.toString()?.replaceAll(',', ';').replaceAll(';;', ';')
    : '';
  dataLayer['search_list_sort_types_available'] = sortByOptions;
  dataLayer['search_total_price_checkbox'] = !isFlexibleDateSearch ? _searchQuery?.includeTaxesAndFees : false;
  dataLayer['search_list_setting_records_per_page'] = _searchQuery?.limit;
  dataLayer['search_list_records_total'] = _searchResults?.total;
  dataLayer['search_list_marshas_on_page'] = marshaListArray?.toString().replaceAll(',', ';');
  dataLayer['search_list_setting_default_sort'] = _searchQuery?.sort?.fields[0]?.field === 'DISTANCE' ? true : false;
  dataLayer['search_currency_type'] = !isFlexibleDateSearch
    ? _searchQuery?.currentCurrency && _searchQuery?.currentCurrency !== 'default'
      ? _searchQuery?.currentCurrency
      : _searchResults?.edges[0]?.node?.rates?.[0]?.rateAmounts?.[0]?.amount?.origin?.currency ?? 'Default'
    : 'Default';
  dataLayer['previous_page'] = window.document?.referrer;
  dataLayer['ram_tile_names'] = datalayerProperties?.NEXT_PUBLIC_RAM_TILE_NAME;
  dataLayer[
    'page_breakpt_orientation'
  ] = `${datalayerProperties?.NEXT_PUBLIC_PAGE_BREAKPT}${screenCategory} - ${window.innerWidth}w: ${deviceOrientation}`;
  dataLayer['page_type'] = window.location?.href;
  dataLayer['search_results_view'] = isMapViewEnabled
    ? datalayerProperties?.NEXT_PUBLIC_SEARCH_MAPVIEW
    : datalayerProperties?.NEXT_PUBLIC_SEARCH_LISTVIEW;
  dataLayer['ensightenEnabled'] = true;
  dataLayer['search_type'] =
    !isAutoSuggestSelectedSearch && !isRecentViewSelectedSearch
      ? 'typed'
      : !isRecentViewSelectedSearch && isAutoSuggestSelectedSearch
      ? 'Selected Recommended Result'
      : !isAutoSuggestSelectedSearch && isRecentViewSelectedSearch
      ? 'Selected Recent Searches'
      : '';
  dataLayer['page_data_layer_ready'] = 'true';
  if (dataLayer['search_result_position_selected']) {
    delete dataLayer['search_result_position_selected'];
  }
  if (
    params.has('unsuccessfulSell') &&
    params.get('unsuccessfulSell') === 'true' &&
    _searchResults?.total &&
    _searchResults?.total > 0
  ) {
    dataLayer['search_sold_out_search'] = 'true';
  }
  const searchCounterValue = sessionStorage.getItem('updateSearchCounter');
  dataLayer['updates_search_call_counter'] = searchCounterValue;
  // Update the window.location.hash with the update search counter
  window.history.replaceState({}, referer as string, `#/${searchCounterValue}/`);
}

export function updateDataLayerObjSigin(
  dataLayer: Record<string, unknown>,
  // isMapViewEnabled: boolean,
  datalayerProperties: any
) {
  let deviceOrientation = datalayerProperties?.NEXT_PUBLIC_ORIENTATION_LANDSCAPE;
  const screenCategory = getScreenCategory();

  if (window.innerWidth < 576 && window.innerHeight > window.innerWidth) {
    deviceOrientation = datalayerProperties?.NEXT_PUBLIC_ORIENTATION_PORTRAIT;
  }
  // ************** ram_tile_names - real logic
  // window.trackAnalyticsObj = JSON.stringify(obj);
  //   //populate Tile Names in dataLayer
  //   if (typeof obj.tiles != "undefined") {
  //     var tempList = "";
  //     $.each(obj.tiles, function(index, value) {
  //       tempList += value.name;
  //       //check for errors, add loaded status
  //       if (typeof value.error !== "undefined") {
  //         tempList += "_Not Loaded|";
  //       } else {
  //         tempList += "_Loaded|";
  //       }
  //       //tempList += value.id.substring(0, value.id.lastIndexOf("_") ) + "|";
  //     });
  //     tempList = tempList.replace(/\|\s*$/, "");
  //     dataLayer.ram_tile_names = tempList;
  //   }

  dataLayer['ensightenEnabled'] = true;
  dataLayer[
    'page_breakpt_orientation'
  ] = `${datalayerProperties?.NEXT_PUBLIC_PAGE_BREAKPT}${screenCategory} - ${window.innerWidth}w: ${deviceOrientation}`;
  // dataLayer['search_results_view'] = isMapViewEnabled
  //   ? datalayerProperties?.NEXT_PUBLIC_SEARCH_MAPVIEW
  //   : datalayerProperties?.NEXT_PUBLIC_SEARCH_LISTVIEW;
  dataLayer['page_type'] = window.location?.href;
  dataLayer['previous_page'] = window.document?.referrer;
  dataLayer['ram_tile_names'] = datalayerProperties?.NEXT_PUBLIC_RAM_TILE_NAME;
}

export function getAllPropertyIDS(arr: any) {
  /**
   * return all property ids from array
   */
  return arr?.map((key: any) => (key.node.id === undefined ? key.node.property.id : key.node.id));
}

export function extractDataFromDTTResponse(
  data: any,
  isDesti: boolean,
  isFlexi: boolean,
  isStateOrCountrySearch?: boolean,
  isAirport?: boolean
) {
  /**
   * receive all type of request data and return required formatted data
   * data = <graphql response>
   * displayAlert = <show alert function>
   */
  let key;
  if (enableHotelList() === true) {
    if (isFlexi) {
      key = GraphQlMgpAPiResponseKey.id;
    } else {
      key = GraphQlMgpAPiResponseKey.id;
    }
  } else {
    if (isStateOrCountrySearch && isDesti && !isAirport) {
      key = GraphQlMgpAPiResponseKey.search_input;
    } else {
      key = isDesti || isAirport ? GraphQlMgpAPiResponseKey.desti : GraphQlMgpAPiResponseKey.geo;
    }
  }
  return isFlexi ? data?.properties?.[key] : data.lowestAvailableRates[key];
}

export function mergeRatesWithBasicInfo(rates: any, infoArr: any) {
  /**
   * rates = <rates data error from LAr>
   * inforArr = <info array from property id info>
   */
  const tempObj = JSON.parse(JSON.stringify(rates));
  const ratesObj = { ...tempObj }; // remove readonly property from obj
  const ratesEdges = ratesObj?.edges?.map((key: any) => {
    if (key.node.property) {
      const id = key.node.property.id;
      const obj = infoArr?.filter((info: any) => info.id === id)[0];
      key.node.property = {
        ...key.node.property,
        ...obj,
      };
      return key;
    } else {
      const id = key.node.id;
      const obj = infoArr?.filter((info: any) => info.id === id)[0];
      key.node = {
        ...key.node,
        ...obj,
      };
      return key;
    }
  });
  ratesObj.edges = ratesEdges;
  return ratesObj;
}

export const fieldsInteractedInSERP = (interactionsSelected: any): any => {
  const fieldsWithTrueVal: string[] = [];

  for (const [key, value] of Object.entries(interactionsSelected)) {
    if (value === true) {
      fieldsWithTrueVal.push(key);
    }
  }

  return fieldsWithTrueVal;
};

export const interactionsAppliedOnSERP = (
  searchQueryState: any,
  formValues: any,
  lengthOfStayValue: number,
  filters: number,
  featureSelected: boolean,
  isFlexibleDateSearch: boolean,
  searchResult: any
): any => {
  const appliedInteractions = {
    brands: Object.keys(searchQueryState?.BRANDS ?? {}),
    includeUnavailableHotels: searchQueryState?.includeUnavailableProperties,
    includeTaxesAndFees: searchQueryState?.includeTaxesAndFees,
    sortByOption: searchQueryState?.sort?.fields[0]?.field,
    pageNumber: searchQueryState?.nextPage / searchQueryState?.limit + 1,
    currencyType: searchQueryState?.currentCurrency,
    priceRange: Object.keys(searchQueryState?.PRICE ?? {}),
    pointsRange: Object.keys(searchQueryState?.POINTS ?? {}),
    distanceRange: Object.keys(searchQueryState?.DISTANCE ?? {}),
    amenities: Object.keys(searchQueryState?.AMENITIES ?? {}),
    transportations: Object.keys(searchQueryState?.TRANSPORTATION_TYPES ?? {}),
    hotelTypes: Object.keys(searchQueryState?.PROPERTY_TYPES ?? {}),
    events: Object.keys(searchQueryState?.MEETINGS_EVENTS ?? {}),
    states: Object.keys(searchQueryState?.STATES ?? {}),
    cities: Object.keys(searchQueryState?.CITIES ?? {}),
    activities: Object.keys(searchQueryState?.ACTIVITIES ?? {}),
    formValues: formValues,
    lengthOfStay: lengthOfStayValue,
    filtersCount: filters,
    featureSelected: featureSelected,
    isFlexibleDateSearch: isFlexibleDateSearch,
    propertyRecordsCount: searchResult?.total ?? 0,
  };

  return appliedInteractions;
};

export function formPageURLQueryString(interactions: any, delimiter: string) {
  return `destinationAddress.destination=${
    interactions?.formValues?.destination?.displayText
  }${delimiter}propertyCode=${delimiter}marriottRewardsNumber=${delimiter}fromDateDefaultFormat=${
    interactions?.formValues?.dates?.fromDate !== undefined
      ? interactions?.formValues?.dates?.fromDate.format('MM/DD/YYYY')
      : ''
  }${delimiter}airportCode=${delimiter}childrenAges=${
    interactions?.formValues?.roomsAndGuests?.childrenAges
  }${delimiter}lengthOfStay=${
    interactions?.formValues?.dates?.fromDate !== undefined && interactions?.formValues?.dates?.toDate !== undefined
      ? interactions?.lengthOfStay
      : 0
  }${delimiter}search-locality=${delimiter}lowestRegularRate=${
    interactions?.formValues?.specialRates?.clusterCode === 'none' ? true : false
  }${delimiter}collapseAccordian=is-hidden${delimiter}t-end=${
    interactions?.formValues?.dates?.toDate !== undefined
      ? interactions?.formValues?.dates?.toDate.format('YYYY-MM-DD')
      : ''
  }${delimiter}isRateCalendar=true${delimiter}isTransient=true${delimiter}numAdultsPerRoom=${
    interactions?.formValues?.roomsAndGuests?.adultsCount
  }${delimiter}destinationAddress.secondaryText=${
    interactions?.formValues?.destinationHiddenFields?.state &&
    interactions?.formValues?.destinationHiddenFields?.country
      ? `${interactions?.formValues?.destinationHiddenFields?.state}, ${interactions?.formValues?.destinationHiddenFields?.country}`
      : `${interactions?.formValues?.destinationHiddenFields?.state ?? ''}` ||
        `${interactions?.formValues?.destinationHiddenFields?.country ?? ''}`
  }${delimiter}toDateDefaultFormat=${
    interactions?.formValues?.dates?.toDate !== undefined
      ? interactions?.formValues?.dates?.toDate.format('MM/DD/YYYY')
      : ''
  }${delimiter}isInternalSearch=true${delimiter}fromToDate=${delimiter}suggestionsPropertyCode=${delimiter}recordsPerPage=40${delimiter}poiCity=${delimiter}incentiveType_Number=${delimiter}destinationAddress.city=${
    interactions?.formValues?.destinationHiddenFields?.city ?? ''
  }${delimiter}destinationAddress.placeId=${
    interactions?.formValues?.destination?.pid
  }${delimiter}destinationAddress.stateProvinceShort=${delimiter}multiRateCorpCodesEntered=${delimiter}isSearch=true${delimiter}multiRateMaxCount=${delimiter}searchType=InCity${delimiter}search-countryRegion=${delimiter}destinationAddress.longitude=${
    interactions?.formValues?.destinationHiddenFields?.longitude ?? ''
  }${delimiter}destinationAddress.name=${delimiter}countryName=${delimiter}roomTypeCode=${delimiter}destinationAddress.latitude=${
    interactions?.formValues?.destinationHiddenFields?.latitude ?? ''
  }${delimiter}clusterCode=${interactions?.formValues?.specialRates?.clusterCode}${delimiter}fromToDate_submit=${
    interactions?.formValues?.dates?.toDate !== undefined
      ? interactions?.formValues?.dates?.toDate.format('MM/DD/YYYY')
      : ''
  }${delimiter}destinationAddress.postalCode=${delimiter}poiName=${delimiter}flexibleDateLowestRateMonth=${delimiter}isHideFlexibleDateCalendar=false${delimiter}t-start=${
    interactions?.formValues?.dates?.fromDate !== undefined
      ? interactions?.formValues?.dates?.fromDate.format('YYYY-MM-DD')
      : ''
  }${delimiter}destinationAddress.cityPopulation=${delimiter}flexibleDateSearchRateDisplay=false${delimiter}destinationAddress.cityPopulationDensity=${delimiter}multiRateClusterCodes=${delimiter}roomCount=${
    interactions?.formValues?.roomsAndGuests?.roomsCount
  }${delimiter}destinationAddress.countryShort=${delimiter}incentiveType=${delimiter}destinationAddress.stateProvinceDisplayName=${delimiter}js-location-nearme-values=${delimiter}destinationAddress.address=${
    interactions?.formValues?.destination?.displayText
  }${delimiter}fromDate=${
    interactions?.formValues?.dates?.fromDate !== undefined
      ? interactions?.formValues?.dates?.fromDate.format('MM/DD/YYYY')
      : ''
  }${delimiter}for-hotels-nearme=Near${delimiter}childrenCount=${
    interactions?.formValues?.roomsAndGuests?.childrenCount
  }${delimiter}searchRadius=${
    interactions?.formValues?.destinationHiddenFields?.radius
  }${delimiter}destinationAddress.locality=${delimiter}flexibleDateSearch=${
    interactions?.formValues?.dates?.fromDate !== undefined && interactions?.formValues?.dates?.toDate !== undefined
      ? interactions?.isFlexibleDateSearch
      : false
  }${delimiter}multiRateCorpCodes=${delimiter}useMultiRateRewardsPoints=${delimiter}toDate=${
    interactions?.formValues?.dates?.toDate !== undefined
      ? interactions?.formValues?.dates?.toDate.format('MM/DD/YYYY')
      : ''
  }${delimiter}vsInitialRequest=false${delimiter}destinationAddress.destinationPageDestinationAddress=${delimiter}initialRequest=true${delimiter}guestCountBox=${
    interactions?.formValues?.roomsAndGuests?.adultsCount
  } Adult Per Room${delimiter}propertyName=${delimiter}singleSearchAutoSuggest=Unmatched${delimiter}showAddressPin=${delimiter}corporateCode=${
    interactions?.formValues?.specialRates?.corporateCode
  }${delimiter}destinationAddress.website=${delimiter}airportName=${delimiter}singleSearch=true${delimiter}autoSuggestItemType=${delimiter}destinationAddress.stateProvince=${
    interactions?.formValues?.destinationHiddenFields?.state ?? ''
  }${delimiter}flexibleDateLowestRateDate=${delimiter}destinationAddress.types=${delimiter}destinationAddress.country=${
    changeCountryCodeForChina(interactions?.formValues?.destinationHiddenFields?.country) ?? ''
  }${delimiter}destinationAddress.mainText=${
    interactions?.formValues?.destinationHiddenFields?.city
  }${delimiter}roomCountBox=${
    interactions?.formValues?.roomsAndGuests?.roomsCount
  } Room${delimiter}isMultiRateSearch=${delimiter}childrenCountBox=${
    interactions?.formValues?.roomsAndGuests?.childrenCount
  } Children Per Room${delimiter}useRewardsPoints=${
    interactions?.formValues?.usePoints?.value
  }${delimiter}rateClusterSetNumType=${delimiter}rateClusterSetNum=`;
}

function checkArrayOfObj(savedProperties: string | any[]) {
  for (let i = 0; i < savedProperties.length; i++) {
    if (typeof savedProperties[i] !== 'string') {
      return false;
    }
  }
  return true;
}

/* update saved props related session variables */
function updateSavedPropsRelatedSessionVariables(
  sessionData: any,
  accessToken: string,
  savedProperties: any,
  AriesCommonObject: any,
  AriesRewardsObject: any
) {
  const isSavedPropertyStringArr = checkArrayOfObj(savedProperties);
  const savedPropertyList: unknown[] = [];
  if (!isSavedPropertyStringArr) {
    savedProperties?.forEach((savedObj: ArrayLike<unknown> | { [s: string]: unknown }) => {
      if (Object.values(savedObj).length) {
        savedPropertyList.push(Object.values(savedObj)[0]);
      }
    });
  }
  if (sessionData?.consumerID && accessToken) {
    if (sessionData?.AriesCommon) {
      AriesCommonObject['AriesCommon.savedProps'] = isSavedPropertyStringArr
        ? String(savedProperties)?.toUpperCase()
        : String(savedPropertyList)?.toUpperCase();
      AriesCommonObject['AriesCommon.savedPropSize'] = String(savedProperties?.length);
      AriesCommonObject['AriesCommon.mr_prof_saved_prop'] = isSavedPropertyStringArr
        ? String(savedProperties)?.toUpperCase()
        : String(savedPropertyList)?.toUpperCase();
      AriesCommonObject['AriesCommon.mr_prof_saved_prop_count'] = String(savedProperties?.length);
      AriesCommonObject['AriesCommon.memState'] = 'authenticated';
    }
    if (sessionData?.AriesRewards) {
      AriesRewardsObject['AriesRewards.savedHotelList'] = isSavedPropertyStringArr
        ? savedProperties
        : savedPropertyList;
    }
  }
}

function updateListOrMapButtonRelatedSessionVariables(isMapView: boolean | undefined, AriesSearchObject: any) {
  if (isMapView) {
    AriesSearchObject['AriesSearch.searchCriteria.searchResultViewType'] = 'map';
  } else if (!isMapView) {
    AriesSearchObject['AriesSearch.searchCriteria.searchResultViewType'] = 'list';
  }
}

// Blocklist of property names that could lead to prototype pollution
const DANGEROUS_KEYS = new Set(['__proto__', 'constructor', 'prototype']);

const isSafePropertyName = (key: string): boolean => !DANGEROUS_KEYS.has(key);

// Sync dot-notation keys on Aries* objects into their nested payloads
export function mergeDotNotationIntoPayload(
  flattenedObject: Record<string, any>,
  payloadObject: any,
  namespace: 'AriesSearch' | 'AriesCommon' | 'AriesRewards'
) {
  if (!payloadObject || !flattenedObject) {
    return payloadObject;
  }

  const prefix = `${namespace}.`;

  Object.entries(flattenedObject).forEach(([key, value]) => {
    if (!key.startsWith(prefix)) {
      return;
    }

    const pathParts = key.replace(prefix, '').split('.');

    // Skip if any path segment is a dangerous property name (prototype pollution prevention)
    if (pathParts.some(segment => !isSafePropertyName(segment))) {
      return;
    }

    let cursor = payloadObject;

    pathParts.forEach((segment, index) => {
      const isLeaf = index === pathParts.length - 1;

      if (isLeaf) {
        if (!Object.prototype.hasOwnProperty.call(cursor, segment) || typeof cursor[segment] !== 'function') {
          cursor[segment] = value;
        }
        return;
      }

      if (!Object.prototype.hasOwnProperty.call(cursor, segment) || typeof cursor[segment] !== 'object') {
        cursor[segment] = Object.create(null);
      }

      const next = cursor[segment];
      // Additional guard: ensure we never traverse into Object.prototype
      if (next === null || next === Object.prototype) {
        return;
      }
      cursor = next;
    });
  });

  return payloadObject;
}

/* update filters related session variables */

function updateFiltersRelatedSessionVariables(
  sessionData: any,
  interactions: any,
  selectedInteractions: any,
  removeBlock: any,
  AriesSearchPayloadObject: any,
  searchQueryState: SearchStateStoreDataType | null,
  isFlexibleDateSearch: boolean,
  AriesSearchObject: any
) {
  AriesSearchPayloadObject.searchCriteria.moreThanTwoFilters = interactions?.filtersCount > 2 ? true : false;
  AriesSearchPayloadObject.searchCriteria.moreThanOneFilter = interactions?.filtersCount > 1 ? true : false;
  AriesSearchObject['AriesSearch.searchCriteria.moreThanTwoFilters'] = interactions?.filtersCount > 2 ? true : false;
  AriesSearchObject['AriesSearch.searchCriteria.moreThanOneFilter'] = interactions?.filtersCount > 1 ? true : false;

  if (!sessionData?.AriesSearch?.propertyFilterCriteria && interactions?.filtersCount > 0) {
    AriesSearchPayloadObject.propertyFilterCriteria = {};
    AriesSearchObject['AriesSearch.propertyFilterCriteria'] = {};
  }

  if (searchQueryState !== null && searchQueryState?.currentConversionRate?.length > 0) {
    const currConversionBlock = searchQueryState?.currentConversionRate;
    // Checking to see if currency is default in which case conversion factor should be 1
    AriesSearchPayloadObject.currentConversionRate =
      searchQueryState?.currentCurrency !== DefaultValue && !isFlexibleDateSearch
        ? currConversionBlock
        : [
            {
              currency: 'default',
              currentConversionRate: 1,
              currentConversionRateDecimalPoint: 0,
            },
          ];

    AriesSearchPayloadObject.currentConversionRateDecimalPoint =
      searchQueryState?.currentCurrency !== DefaultValue && !isFlexibleDateSearch
        ? currConversionBlock
        : [
            {
              currency: 'default',
              currentConversionRate: 1,
              currentConversionRateDecimalPoint: 0,
            },
          ];
    AriesSearchObject['AriesSearch.currentConversionRate'] =
      searchQueryState?.currentCurrency !== DefaultValue && !isFlexibleDateSearch
        ? currConversionBlock
        : [
            {
              currency: 'default',
              currentConversionRate: 1,
              currentConversionRateDecimalPoint: 0,
            },
          ];
    AriesSearchObject['AriesSearch.currentConversionRateDecimalPoint'] =
      searchQueryState?.currentCurrency !== DefaultValue && !isFlexibleDateSearch
        ? currConversionBlock
        : [
            {
              currency: 'default',
              currentConversionRate: 1,
              currentConversionRateDecimalPoint: 0,
            },
          ];
  }

  const facets =
    interactions?.brands?.length ||
    interactions?.amenities?.length ||
    interactions?.transportations?.length ||
    interactions?.hotelTypes?.length ||
    interactions?.events?.length ||
    interactions?.states?.length ||
    interactions?.cities?.length ||
    interactions?.activities?.length;

  const rangeFilters =
    interactions?.priceRange?.length || interactions?.pointsRange?.length || interactions?.distanceRange?.length;

  // Update propertyFilterCriteria based on selectedInteractions
  if (
    facets > 0 &&
    selectedInteractions?.length > 0 &&
    sessionData?.AriesSearch?.propertyFilterCriteria &&
    AriesSearchPayloadObject?.propertyFilterCriteria &&
    AriesSearchPayloadObject?.propertyFilterCriteria?.facets &&
    Object.keys(AriesSearchPayloadObject?.propertyFilterCriteria?.facets)?.length
  ) {
    for (const originalKey of selectedInteractions) {
      const key = keyMappings[originalKey] || originalKey;
      if (key in AriesSearchPayloadObject.propertyFilterCriteria.facets) {
        AriesSearchPayloadObject.propertyFilterCriteria.facets[key] = [];
        delete AriesSearchPayloadObject.propertyFilterCriteria.facets[key];
      }
    }
  } else if (selectedInteractions?.length > 0 && facets > 0) {
    AriesSearchPayloadObject.propertyFilterCriteria.facets = {};
  }

  if (rangeFilters > 0) {
    AriesSearchPayloadObject.propertyFilterCriteria.rangeFilters = {};
  }

  if (rangeFilters === 0 && sessionData?.AriesSearch?.propertyFilterCriteria?.rangeFilters) {
    AriesSearchObject['AriesSearch.propertyFilterCriteria'] = { rangeFilters: {} };
  }

  if (facets === 0 && sessionData?.AriesSearch?.propertyFilterCriteria?.facets) {
    AriesSearchObject['AriesSearch.propertyFilterCriteria'] = { facets: {} };
  }

  if (facets === 0 && rangeFilters === 0 && sessionData?.AriesSearch?.propertyFilterCriteria) {
    removeBlock?.push('AriesSearch.propertyFilterCriteria');
  }
}

/* update availability filter related session variables */
function updateAvailabilityFiltersRelatedSessionVariables(interactions: any, AriesSearchObject: any) {
  AriesSearchObject['AriesSearch.searchCriteria.availabilityRequestVO.excludeUnavailableProperties'] =
    !interactions?.includeUnavailableHotels;
  AriesSearchObject['AriesSearch.searchCriteria.showAllHotel'] = interactions?.includeUnavailableHotels;
  AriesSearchObject['AriesSearch.searchCriteria.showAvailableHotels'] = !interactions?.includeUnavailableHotels;
}

/* update price filter related session variables */
function updatePriceFiltersRelatedSessionVariables(interactions: any, AriesSearchObject: any) {
  if (interactions?.priceRange?.length) {
    AriesSearchObject['AriesSearch.propertyFilterCriteria'] = {
      ...AriesSearchObject['AriesSearch.propertyFilterCriteria'],
      rangeFilters: {
        ...AriesSearchObject['AriesSearch.propertyFilterCriteria']?.rangeFilters,
        price: interactions?.priceRange,
      },
    };
  } else {
    delete AriesSearchObject?.propertyFilterCriteria?.rangeFilters?.price;
  }
}

/* update points filter related session variables */
function updatePointsFiltersRelatedSessionVariables(interactions: any, AriesSearchObject: any) {
  if (interactions?.pointsRange?.length) {
    AriesSearchObject['AriesSearch.propertyFilterCriteria'] = {
      ...AriesSearchObject['AriesSearch.propertyFilterCriteria'],
      rangeFilters: {
        ...AriesSearchObject['AriesSearch.propertyFilterCriteria']?.rangeFilters,
        points: interactions?.pointsRange,
      },
    };
  } else {
    delete AriesSearchObject?.propertyFilterCriteria?.rangeFilters?.points;
  }
}

/* update amenities filter related session variables */
function updateAmenitiesFiltersRelatedSessionVariables(interactions: any, AriesSearchObject: any) {
  if (interactions?.amenities?.length) {
    AriesSearchObject['AriesSearch.propertyFilterCriteria'] = {
      ...AriesSearchObject['AriesSearch.propertyFilterCriteria'],
      facets: {
        ...AriesSearchObject['AriesSearch.propertyFilterCriteria']?.facets,
        amenities: interactions?.amenities,
      },
    };
  } else {
    delete AriesSearchObject?.propertyFilterCriteria?.facets?.amenities;
  }
}

/* update brands filter related session variables */
function updateBrandsFiltersRelatedSessionVariables(interactions: any, AriesSearchObject: any) {
  if (interactions?.brands?.length) {
    AriesSearchObject['AriesSearch.propertyFilterCriteria'] = {
      ...AriesSearchObject['AriesSearch.propertyFilterCriteria'],
      facets: { ...AriesSearchObject['AriesSearch.propertyFilterCriteria']?.facets, brands: interactions?.brands },
    };
    AriesSearchObject['AriesSearch.searchCriteria.brandCodes'] = interactions?.brands;
  } else {
    // Remove the 'brands' key from facets
    delete AriesSearchObject?.propertyFilterCriteria?.facets?.brands;
  }
}

/* update distance filter related session variables */
function updateDistanceFiltersRelatedSessionVariables(interactions: any, AriesSearchObject: any) {
  if (interactions?.distanceRange?.length) {
    AriesSearchObject['AriesSearch.propertyFilterCriteria'] = {
      ...AriesSearchObject['AriesSearch.propertyFilterCriteria'],
      rangeFilters: {
        ...AriesSearchObject['AriesSearch.propertyFilterCriteria']?.rangeFilters,
        distance: interactions?.distanceRange,
      },
    };
  } else {
    delete AriesSearchObject?.propertyFilterCriteria?.rangeFilters?.distance;
  }
}

/* update transportation filter related session variables */
function updateTransportationFiltersRelatedSessionVariables(interactions: any, AriesSearchObject: any) {
  if (interactions?.transportations?.length) {
    AriesSearchObject['AriesSearch.propertyFilterCriteria'] = {
      ...AriesSearchObject['AriesSearch.propertyFilterCriteria'],
      facets: {
        ...AriesSearchObject['AriesSearch.propertyFilterCriteria']?.facets,
        'transportation-types': interactions?.transportations,
      },
    };
  } else {
    delete AriesSearchObject?.propertyFilterCriteria?.facets?.['transportation-types'];
  }
}

/* update hotel type filter related session variables */
function updateHotelTypeFiltersRelatedSessionVariables(interactions: any, AriesSearchObject: any) {
  if (interactions?.hotelTypes?.length) {
    AriesSearchObject['AriesSearch.propertyFilterCriteria'] = {
      ...AriesSearchObject['AriesSearch.propertyFilterCriteria'],
      facets: {
        ...AriesSearchObject['AriesSearch.propertyFilterCriteria']?.facets,
        'property-types': interactions?.hotelTypes,
      },
    };
  } else {
    delete AriesSearchObject?.propertyFilterCriteria?.facets?.['property-types'];
  }
}

/* update event type filter related session variables */
function updateEventsFiltersRelatedSessionVariables(interactions: any, AriesSearchObject: any) {
  if (interactions?.events?.length) {
    AriesSearchObject['AriesSearch.propertyFilterCriteria'] = {
      ...AriesSearchObject['AriesSearch.propertyFilterCriteria'],
      facets: {
        ...AriesSearchObject['AriesSearch.propertyFilterCriteria']?.facets,
        'meetings-and-events': interactions?.events,
      },
    };
  } else {
    delete AriesSearchObject?.propertyFilterCriteria?.facets?.['meetings-and-events'];
  }
}

/* update activities filter related session variables */
function updateActivitiesFiltersRelatedSessionVariables(interactions: any, AriesSearchObject: any) {
  if (interactions?.activities?.length) {
    AriesSearchObject['AriesSearch.propertyFilterCriteria'] = {
      ...AriesSearchObject['AriesSearch.propertyFilterCriteria'],
      facets: {
        ...AriesSearchObject['AriesSearch.propertyFilterCriteria']?.facets,
        'activities-on-site': interactions?.activities,
      },
    };
  } else {
    delete AriesSearchObject?.propertyFilterCriteria?.facets?.['activities-on-site'];
  }
}

/* update states filter related session variables */
function updateStatesFiltersRelatedSessionVariables(interactions: any, AriesSearchObject: any) {
  if (interactions?.states?.length) {
    AriesSearchObject['AriesSearch.propertyFilterCriteria'] = {
      ...AriesSearchObject['AriesSearch.propertyFilterCriteria'],
      facets: { ...AriesSearchObject['AriesSearch.propertyFilterCriteria']?.facets, states: interactions?.states },
    };
  } else {
    delete AriesSearchObject?.propertyFilterCriteria?.facets?.states;
  }
}

/* update city filter related session variables */
function updateCityFiltersRelatedSessionVariables(interactions: any, AriesSearchObject: any) {
  if (interactions?.cities?.length) {
    AriesSearchObject['AriesSearch.propertyFilterCriteria'] = {
      ...AriesSearchObject['AriesSearch.propertyFilterCriteria'],
      facets: { ...AriesSearchObject['AriesSearch.propertyFilterCriteria']?.facets, cities: interactions?.cities },
    };
  } else {
    delete AriesSearchObject?.propertyFilterCriteria?.facets?.cities;
  }
}

/* update pagination related session variables */
function updatePaginationFiltersRelatedSessionVariables(interactions: any, AriesSearchObject: any) {
  AriesSearchObject['AriesSearch.searchCriteria.paginationAndSortingRequestVO.pageNumber'] = interactions?.pageNumber;
}

/* update taxes and fees related session variables */
function updateTaxesAndFeesFiltersRelatedSessionVariables(interactions: any, AriesSearchObject: any) {
  AriesSearchObject['AriesSearch.searchCriteria.showFullPrice'] = String(
    interactions?.includeTaxesAndFees
  )?.toLowerCase();
}

/* update currency conversion filter related session variables */
function updateCurrencyFiltersRelatedSessionVariables(interactions: any, AriesSearchObject: any) {
  const currentCurrency = interactions?.currencyType;
  if (currentCurrency?.toLowerCase() === DefaultValue) {
    AriesSearchObject['AriesSearch.searchCriteria.selectedCurrency'] = '';
  } else {
    AriesSearchObject['AriesSearch.searchCriteria.selectedCurrency'] = `${currentCurrency
      ?.charAt(0)
      ?.toUpperCase()}${currentCurrency?.slice(1)}`;
  }
}

/* update sort by related session variables */
function updateSortByFiltersRelatedSessionVariables(interactions: any, AriesSearchObject: any) {
  AriesSearchObject['AriesSearch.searchCriteria.paginationAndSortingRequestVO.sortType'] =
    interactions?.sortByOption?.toLowerCase();
}

/* call and update datalayer on any changes in SERP */
export async function callDataLayerOnFeatureChanges(
  resolvedUrl: any,
  headersData: any,
  query: any,
  datalayerParams: any,
  sessionID: string,
  searchQueryId?: string,
  currentLocale?: string,
  searchDestinationTypeForDatalayer?: string,
  recipeID?: string,
  isSearchFallback?: boolean,
  searchFallbackType?: string,
  clusterCode?: string
): Promise<void | unknown> {
  const getPageURI = resolvedUrl ? resolvedUrl.split('?')[0] : '';
  const MOCK_SESSION_ID = '825167AA-9F54-5B1B-95C0-53EEC4E22391';
  const absolutePageURL = `${getPageURI}.mi`;
  const localeKeyValue = currentLocale?.replace('_', '-') ?? 'en-US';
  const channelValue = datalayerParams?.NEXT_PUBLIC_DATALAYER_SITENAME?.split('.')[0];
  const datalayerUrl = datalayerParams?.MI_DATALAYER_APP_URL || '';
  try {
    const getHeaders = (): string => {
      let cookieValues = '';
      if (headersData?.mi_site) {
        cookieValues = cookieValues + ` MI_SITE=${headersData?.mi_site};`;
      }
      if (headersData?.mi_visitor) {
        cookieValues = cookieValues + ` MI_VISITOR=${headersData?.mi_visitor};`;
      }
      if (headersData?.remembermeuserid) {
        cookieValues = cookieValues + ` RememberMeUserID=${encodeURIComponent(headersData?.remembermeuserid)};`;
      }
      if (headersData?.remembermealternateid) {
        cookieValues =
          cookieValues + ` RememberMeAlternateID=${encodeURIComponent(headersData?.remembermealternateid)};`;
      }
      if (headersData?.useridtoken) {
        cookieValues = cookieValues + ` UserIdToken=${encodeURIComponent(headersData?.useridtoken)};`;
      }
      return cookieValues;
    };

    const headersKey: Record<string, string | undefined> = {};

    // creating headers for datalayer call
    if (headersData && Object.keys(headersData).length > 0) {
      DATALAYER_CONSTANT.headerKeys.forEach(name => {
        if (name && headersData[name as KeysRequest['keys']]) {
          headersKey[name] = headersData[name as KeysRequest['keys']];
        }
      });
    }

    let acceptLangHeader;
    if (headersKey['accept-language']) {
      acceptLangHeader = headersKey['accept-language'];
      delete headersKey['accept-language'];
    }
    const reqBody = {
      context: {
        localeKey: localeKeyValue,
        programFlag: '',
        siteName: datalayerParams?.NEXT_PUBLIC_DATALAYER_SITENAME,
        mobileAuthEnabled: 'false',
        productSiteId: datalayerParams?.NEXT_PUBLIC_DATALAYER_PRODUCT,
        channel: channelValue,
        pageContent: [],
        pageURI: getPageURI,
        referer: headersData?.referer,
        absolutePageURL: absolutePageURL,
        applicationName: datalayerParams?.NEXT_PUBLIC_DATALAYER_APPLICATION,
        products: datalayerParams?.NEXT_PUBLIC_DATALAYER_PRODUCT,
        template: datalayerParams?.NEXT_PUBLIC_DATALAYER_TEMPLATE,
        seoQueryParams: query,
        fusionSearchId: searchQueryId,
        isDtt: true,
        isSearchFallback: isSearchFallback,
        searchFallbackType: searchFallbackType,
      },
      sessionToken: sessionID ?? MOCK_SESSION_ID,
      sourceURI: absolutePageURL,
      variation: datalayerParams?.NEXT_PUBLIC_DATALAYER_VARIATION,
    };

    // datalayer call
    const dataLayer = await Axios.post(datalayerUrl, reqBody, {
      headers: {
        // please make sure these coookies are in single line. Else there will be `TypeError [ERR_INVALID_CHAR]`
        cookie: `sessionID=${sessionID}; RememberMeFlag=${headersData?.remembermeflag}; ${getHeaders()}`,
        'Accept-Language': getLanguage() || acceptLangHeader || NEXT_PUBLIC_DEFAULT_LANG || '',
        ...headersKey,
      },
    });

    log.debug(`${headersKey}, 'headersKey headersKey`);
    log.debug(`==========datalayerUrl==========: ${datalayerUrl}`);
    log.debug(`==========reqBody==========: ${JSON.stringify(reqBody)}`);
    log.debug(`==========response==========: ${JSON.stringify(dataLayer)}`);

    const datalayerResponse = dataLayer?.data?.component?.data?.dataProperties;

    delete window?.dataLayer?.['search_sort_id'];
    if (recipeID && window.dataLayer) {
      window.dataLayer['search_sort_id'] = recipeID;
    }

    if (datalayerResponse?.length && window?.dataLayer) {
      // This logic is implemented to remove the search_destination_type if we dont get that in Datalayer response
      const isDestinationTypePresent = datalayerResponse.find((item: any) => item.key === 'search_destination_type');
      if (
        !isDestinationTypePresent &&
        window.dataLayer['search_destination_type'] &&
        !searchDestinationTypeForDatalayer
      ) {
        delete window.dataLayer['search_destination_type'];
      } else {
        window.dataLayer['search_destination_type'] = searchDestinationTypeForDatalayer;
      }
      const isSearchFallbackPresent = datalayerResponse.find((item: any) => item.key === 'search_fallback_exists');
      // Preserve search_fallback_exists when the fallback type is HQV_SESSION_ID_FAILURE
      // so the sessionId fallback tracking persists across subsequent datalayer updates.
      if (
        !isSearchFallbackPresent &&
        window.dataLayer['search_fallback_exists'] &&
        window.dataLayer['search_fallback_type'] !== HQV_SESSION_ID_FAILURE
      ) {
        delete window.dataLayer['search_fallback_exists'];
      }
      const isSearchFallbackTypePresent = datalayerResponse.find((item: any) => item.key === 'search_fallback_type');
      // Preserve search_fallback_type when it is HQV_SESSION_ID_FAILURE
      // to prevent subsequent datalayer calls from clearing the sessionId fallback indicator.
      if (
        !isSearchFallbackTypePresent &&
        window.dataLayer['search_fallback_type'] &&
        window.dataLayer['search_fallback_type'] !== HQV_SESSION_ID_FAILURE
      ) {
        delete window.dataLayer['search_fallback_type'];
      }

      if (clusterCode !== 'none' && !window.dataLayer['search_cluster_code']) {
        window.dataLayer['search_cluster_code'] = clusterCode;
      }

      // Check for warn rate 1000 error
      if (
        window.dataLayer['search_cluster_code'] &&
        (clusterCode === 'none' || searchFallbackType?.includes(WARN_CODE_1000))
      ) {
        delete window.dataLayer['search_cluster_code'];
      }
      for (const [key] of Object.entries(window.dataLayer)) {
        datalayerResponse?.forEach((entry: any) => {
          if (window.dataLayer) {
            if (key === entry.key) {
              window.dataLayer[key] = entry.value;
            } else {
              window.dataLayer[entry.key] = entry.value;
            }
          }
        });
      }
    }
    return; // Explicit return for success case
  } catch (error) {
    return error;
  }
}

export async function updateSessionDocProperties(
  sessionID: string,
  accessToken: string,
  sessionURL: string,
  datalayerParams: any,
  searchQueryState: SearchStateStoreDataType | null,
  savedProperties?: any,
  interactions?: any,
  resolvedUrl?: any,
  headersData?: any,
  query?: any,
  selectedInteractions?: any,
  isMapView?: boolean | undefined,
  fusionSearchId?: string,
  setViewChangeButtonClicked?: boolean,
  currentLocale?: string,
  searchDestinationTypeForSession?: string
) {
  const searchQueryId = fusionSearchId;
  const requestId = useClientEnvVarsStore.getState().envVarsObject['REQUEST_ID'];
  const removeBlock: any = [];
  let postCallTriggered = false;
  let postPayload: any = {};
  let response: any = {};

  try {
    if (
      interactions?.featureSelected ||
      (selectedInteractions?.length &&
        (selectedInteractions.includes('savedProperties') ||
          selectedInteractions.includes('listOrMapButton') ||
          (selectedInteractions.includes('currencyConversion') && interactions?.currencyType === DefaultValue))) ||
      interactions?.isFlexibleDateSearch
    ) {
      response = await fetchSessionData(sessionURL, sessionID, requestId);
      const sessionDoc = response?.data;
      const AriesCommonPayloadObject = sessionDoc?.cacheData?.data?.AriesCommon;
      const AriesSearchPayloadObject = sessionDoc?.cacheData?.data?.AriesSearch;
      const AriesRewardsPayloadObject = sessionDoc?.cacheData?.data?.AriesRewards;
      const AriesSearchObject: any = {};
      const AriesCommonObject: any = {};
      const AriesRewardsObject: any = {};

      updateFiltersRelatedSessionVariables(
        sessionDoc?.cacheData?.data,
        interactions,
        selectedInteractions,
        removeBlock,
        AriesSearchPayloadObject,
        searchQueryState,
        interactions?.isFlexibleDateSearch,
        AriesSearchObject
      );
      log.debug('values inside array', selectedInteractions);

      selectedInteractions?.forEach((value: any) => {
        switch (value) {
          case 'availability':
            updateAvailabilityFiltersRelatedSessionVariables(interactions, AriesSearchObject);
            break;
          case 'price':
            updatePriceFiltersRelatedSessionVariables(interactions, AriesSearchObject);
            break;
          case 'points':
            updatePointsFiltersRelatedSessionVariables(interactions, AriesSearchObject);
            break;
          case 'amenities':
            updateAmenitiesFiltersRelatedSessionVariables(interactions, AriesSearchObject);
            break;
          case 'brands':
            updateBrandsFiltersRelatedSessionVariables(interactions, AriesSearchObject);
            break;
          case 'distance':
            updateDistanceFiltersRelatedSessionVariables(interactions, AriesSearchObject);
            break;
          case 'transportation':
            updateTransportationFiltersRelatedSessionVariables(interactions, AriesSearchObject);
            break;
          case 'hotelType':
            updateHotelTypeFiltersRelatedSessionVariables(interactions, AriesSearchObject);
            break;
          case 'events':
            updateEventsFiltersRelatedSessionVariables(interactions, AriesSearchObject);
            break;
          case 'activities':
            updateActivitiesFiltersRelatedSessionVariables(interactions, AriesSearchObject);
            break;
          case 'states':
            updateStatesFiltersRelatedSessionVariables(interactions, AriesSearchObject);
            break;
          case 'city':
            updateCityFiltersRelatedSessionVariables(interactions, AriesSearchObject);
            break;
          case 'pagination':
            updatePaginationFiltersRelatedSessionVariables(interactions, AriesSearchObject);
            break;
          case 'taxesAndFees':
            updateTaxesAndFeesFiltersRelatedSessionVariables(interactions, AriesSearchObject);
            break;
          case 'currencyConversion':
            updateCurrencyFiltersRelatedSessionVariables(interactions, AriesSearchObject);
            break;
          case 'sortBy':
            updateSortByFiltersRelatedSessionVariables(interactions, AriesSearchObject);
            break;
          case 'quickFilters':
            if (interactions?.hotelTypes?.length) {
              updateHotelTypeFiltersRelatedSessionVariables(interactions, AriesSearchObject);
            }
            if (interactions?.amenities?.length) {
              updateAmenitiesFiltersRelatedSessionVariables(interactions, AriesSearchObject);
            }
            break;
          case 'savedProperties':
            updateSavedPropsRelatedSessionVariables(
              sessionDoc?.cacheData?.data,
              accessToken,
              savedProperties,
              AriesCommonObject,
              AriesRewardsObject
            );
            break;
          case 'listOrMapButton':
            updateListOrMapButtonRelatedSessionVariables(isMapView, AriesSearchObject);
            break;
          default:
            log.debug('None of the above cases matched', value);
            break;
        }
      });

      if (sessionDoc?.cacheData?.data?.consumerID && accessToken === null) {
        AriesCommonObject['AriesCommon.memState'] = 'remembered';
      }

      if (!sessionDoc?.cacheData?.data?.consumerID && !accessToken) {
        AriesCommonObject['AriesCommon.memState'] = 'unauthenticated';
      }
      if (sessionDoc?.cacheData?.data?.consumerID && accessToken) {
        if (AriesRewardsPayloadObject?.savedHotelList) {
          AriesRewardsObject['AriesRewards.savedHotelList'] = AriesRewardsPayloadObject?.savedHotelList;
        }
        if (AriesCommonPayloadObject?.savedProps) {
          AriesCommonObject['AriesCommon.savedProps'] = AriesCommonPayloadObject?.savedProps;
        }
        if (AriesCommonPayloadObject?.savedPropSize) {
          AriesCommonObject['AriesCommon.savedPropSize'] = AriesCommonPayloadObject?.savedPropSize;
        }
        if (AriesCommonPayloadObject?.mr_prof_saved_prop) {
          AriesCommonObject['AriesCommon.mr_prof_saved_prop'] = AriesCommonPayloadObject?.mr_prof_saved_prop;
        }
        if (AriesCommonPayloadObject?.mr_prof_saved_prop_count) {
          AriesCommonObject['AriesCommon.mr_prof_saved_prop_count'] =
            AriesCommonPayloadObject?.mr_prof_saved_prop_count;
        }
      }
      if (searchDestinationTypeForSession) {
        AriesSearchObject['AriesSearch.searchCriteria.address.destinationType'] = searchDestinationTypeForSession;
      }
      // Sync dot-notation keys on Aries* objects into their nested payloads to ensure the "postPayload" is updated correctly
      mergeDotNotationIntoPayload(AriesSearchObject, AriesSearchPayloadObject, 'AriesSearch');
      mergeDotNotationIntoPayload(AriesCommonObject, AriesCommonPayloadObject, 'AriesCommon');
      mergeDotNotationIntoPayload(AriesRewardsObject, AriesRewardsPayloadObject, 'AriesRewards');

      await Axios.patch(
        sessionURL,
        {
          createOrUpdate: { ...AriesSearchObject, ...AriesCommonObject, ...AriesRewardsObject },
          remove: removeBlock,
          sessionToken: sessionID,
        },
        {
          headers: {
            Cookie: `sessionID=${sessionID}`,
            'Access-Control-Allow-Origin': '*',
            'Content-Type': 'application/json',
            'x-request-id': requestId,
          },
        }
      );
      postCallTriggered = true;
      postPayload = sessionDoc;
      postPayload.cacheData.data.AriesCommon = AriesCommonPayloadObject;
      postPayload.cacheData.data.AriesSearch = AriesSearchPayloadObject;
      postPayload.cacheData.data.AriesRewards = AriesRewardsPayloadObject;
    }

    if (postCallTriggered && setViewChangeButtonClicked) {
      // call datalayer update if session is updated(using postCallTriggered flag) and list/map view has changed OR currency has been updated
      const clusterCode =
        response?.data?.cacheData?.data?.AriesSearch?.searchCriteria?.availabilityRequestVO?.clusterCode;
      callDataLayerOnFeatureChanges(
        resolvedUrl,
        headersData,
        query,
        datalayerParams,
        sessionID,
        searchQueryId,
        currentLocale,
        undefined,
        undefined,
        undefined,
        undefined,
        clusterCode
      );
    }

    if (postCallTriggered) {
      return postPayload;
    } else {
      return response?.data;
    }
  } catch (error) {
    // ignore
    return error;
  }
}

export async function getDestinationHiddenFilterData(
  hiddenData: any,
  isGeographicSearchEnabled: any
): Promise<SearchStateExtendsType> {
  log.debug(
    hiddenData.state
      ? {
          [SearchFacetsList.states]: {
            [hiddenData.state]: hiddenData.state,
          },
        }
      : {
          [SearchFacetsList.countries]: {
            [hiddenData.country]: hiddenData.country,
          },
        },
    'dest'
  );
  if (hiddenData.state || hiddenData.stateProvince) {
    if (hiddenData.state) {
      return {
        [SearchFacetsList.states]: {
          [hiddenData.state]: hiddenData.state,
        },
        ...(!isGeographicSearchEnabled
          ? {}
          : {
              [SearchFacetsList.countries]: {
                [hiddenData.country]: hiddenData.country,
              },
            }),
      };
    } else {
      const country = hiddenData.country;
      return {
        [SearchFacetsList.states]: {
          [hiddenData.stateProvince]: hiddenData.stateProvince,
        },
        ...(!isGeographicSearchEnabled
          ? {}
          : {
              [SearchFacetsList.countries]: {
                [country]: country,
              },
            }),
      };
    }
  } else if (hiddenData.region) {
    return {
      [SearchFacetsList.leisureRegions]: {
        [hiddenData.region?.toLowerCase()?.split(' ').join('-')]: hiddenData.region
          ?.toLowerCase()
          ?.split(' ')
          .join('-'),
      },
    };
  } else {
    const country = hiddenData.country;
    return {
      [SearchFacetsList.countries]: {
        [country]: country,
      },
    };
  }
}

// fetch strikethrough special offered rate block from graphql object on the basis of rate category and memberLevel
export function getCurrentRateStrikeThroughBlock(
  rateBlockArr?: Array<SearchRateType>
): SearchRateAmountType | null | undefined {
  if (!rateBlockArr) {
    return null;
  }
  if (rateBlockArr.length > 1) {
    // get strikethorugh price from the same block that we are going to use for price
    const rateBlock = rateBlockArr?.filter(
      (key: SearchRateType) =>
        key.rateCategory?.value &&
        EEO_MEMBER_CODE.indexOf(key.rateCategory?.value) > -1 &&
        key.rateCategory?.code === SPECIAL_RATES_CODE
    )[0];
    return getCurrRateAmountBlock(rateBlock);
  }
  return null;
}

//check Strikethorugh percentage threshold is matching or not
export function checkThresholdPercentage(standardPrice: number, eeoPrice: number): boolean {
  let thresholdFlag = false;
  let percentage = 0;
  let total = 0;

  percentage = (standardPrice * STRIKE_PRICE_THRESHOLD_PERCENTAGE) / 100;
  total = standardPrice - eeoPrice;
  if (total >= percentage) {
    thresholdFlag = true;
  }
  return thresholdFlag;
}

export function getCurrencyOriginAccordToLocale(node: any): SearchRateMonetaryAmountType | undefined | null {
  return node?.origin;
}

interface arrayFilters {
  amenities?: Array<string | number>;
  transportationTypes?: Array<string | number>;
  brands?: Array<string | number>;
  hotelTypes?: Array<string | number>;
  activities?: Array<string | number>;
  events?: Array<string | number>;
  states?: Array<string | number>;
  cities?: Array<string | number>;
  distanceRangeIndex?: string | number[];
  priceRangeIndex?: string | number[];
  pointsRangeIndex?: string | number[];
}

interface searchFilters extends arrayFilters {
  includeTaxes?: boolean;
  showAvailableHotels?: boolean;
  selectedCurrency?: string;
}

/**
 * Method to update the query parameters in the URL
 * @param filtersSelected {object}
 * @param filtersToUpdate {Array<string>}
 * @returns {void}
 */
export function updateQueryParams(
  filtersSelected: searchFilters | any,
  filtersToUpdate: Array<string>,
  referer: string
): void {
  const params = new URLSearchParams(window.location.search);
  for (const filter of filtersToUpdate) {
    if (filter === 'includeTaxes' || filter === 'showAvailableHotels' || filter === 'selectedCurrency') {
      params.set(searchFilters[filter], String(filtersSelected?.[filter]));
    } else {
      if (filtersSelected?.[filter as keyof arrayFilters]?.length) {
        if (filter === 'distanceRangeIndex' || filter === 'priceRangeIndex' || filter === 'pointsRangeIndex') {
          params.set(searchFilters[filter], String(!filtersSelected?.[filter] ? 0 : filtersSelected?.[filter]));
        } else {
          params.set(
            searchFilters[filter as keyof arrayFilters],
            filtersSelected?.[filter as keyof arrayFilters]?.join(',') as string
          );
        }
      } else if (enableBoostBrands() === true && searchFilters[filter as keyof arrayFilters] === 'marriottBrands') {
        const brandsList = params?.getAll('marriottBrands')?.toString();
        params.set(searchFilters[filter as keyof searchFilters], brandsList);
        params.set('showMore', 'true');
      } else {
        params.delete(searchFilters[filter as keyof searchFilters]);
      }
    }
  }
  // Delete the filterApplied query param when user applies/modifies the filters from SERP
  params.delete('filterApplied');

  if (filtersSelected?.selectedCurrency) {
    window.history.replaceState({}, referer, decodeURIComponent(`?${params}${window.location.hash}`));
  } else {
    window.history.replaceState({}, referer, decodeURIComponent(`?${params}`));
  }
}

/**
 * Method to update the query parameters in the URL
 * @param SessionID {String}
 * @returns SessionObject
 */
export async function getHQVSessionData(sessionID: string, sessionURL: string) {
  const requestId = useClientEnvVarsStore.getState().envVarsObject['REQUEST_ID'];
  try {
    const response = await fetchSessionData(sessionURL, sessionID, requestId);
    return response.data;
  } catch (error) {
    return error;
  }
}

/**
 * Method to get the rank of an index
 * @param index {number}
 * @returns string
 */
export function getIndexOfPropertyCard(index?: number): string {
  const idx = index ?? 0;
  if ((idx + 1) % 40 === 1 || (idx + 1) % 40 === 21 || (idx + 1) % 40 === 31) {
    return 'st';
  } else if ((idx + 1) % 40 === 2 || (idx + 1) % 40 === 22 || (idx + 1) % 40 === 32) {
    return 'nd';
  } else if ((idx + 1) % 40 === 3 || (idx + 1) % 40 === 23 || (idx + 1) % 40 === 33) {
    return 'rd';
  } else return 'th';
}

/**
 * Method to add datalayer attribute on click of view rates button on property card
 * @param searchResult {any}
 * @param pageNumber {number}
 * @param index {number}
 * @param isFlexibleDateSearch {boolean}
 * @returns void
 */
export function updateDatalayerObjViewRates(
  searchResult?: any,
  pageNumber?: number,
  index?: number,
  isFlexibleDateSearch?: boolean,
  HQVRateBlockFlag: boolean = false
) {
  const idx = index ?? 0;
  const rank = getIndexOfPropertyCard(index);
  if (!isFlexibleDateSearch && !HQVRateBlockFlag) {
    const search_result_position_selected = `page ${pageNumber}|${
      searchResult?.pageInfo?.hasNextPage === true ? searchResult?.pageInfo?.nextOffset : searchResult?.total
    } results per page|${idx + 1}${rank} selected`;
    sessionStorage.setItem('search_result_position_selected', search_result_position_selected);
  }
}

/**
 * Method to check boost brands in the URL
 * @returns boolean
 */
export function enableBoostBrands(): boolean {
  const params = new URLSearchParams(window.location.search);
  const hasBrandsParam = params?.has('marriottBrands') && params?.getAll('marriottBrands')?.length > 0;
  const hasShowMoreParam = params?.has('showMore') && params?.get('showMore') === 'true';

  if (hasBrandsParam && hasShowMoreParam) return true;
  else return false;
}

/**
 * Method to check if the unsuccessful user journey alert should be displayed
 * @returns boolean
 */
export function isUnsuccessfulUserJourney(): boolean {
  if (typeof window !== 'undefined') {
    const params = new URLSearchParams(window.location.search);
    const unsuccessfulSellFlag = params?.has('unsuccessfulSell') && params?.get('unsuccessfulSell') === 'true';
    return unsuccessfulSellFlag;
  } else {
    return false;
  }
}

/**
 * Method to check hotel list params in the url
 * @returns boolean
 */
export function enableHotelList(): boolean {
  if (typeof window !== 'undefined') {
    const params = new URLSearchParams(window.location.search);
    const hasHotelsParam =
      params?.has('searchType') &&
      (params?.get('searchType') === 'MiniStoresHotelList' || params?.get('searchType') === 'HotelList');
    const hasHotelListParam = params?.has('hotelList') && params?.getAll('hotelList')?.length > 0;

    if (hasHotelsParam && hasHotelListParam) return true;
    else return false;
  } else {
    return false;
  }
}

/**
 * Method to clear the couchbase and show favorite properties instantly
 * @param consumerID {string}
 * @param url {string}
 * @param loggerApiURL {string}
 * @returns SavedPropertiesObject
 */
export const clearCouchBaseCache = async (consumerID: string, url: string, loggerApiURL: string) => {
  let result;
  try {
    result = await Axios.post(url, {
      consumerID,
    });
    return result;
  } catch (err) {
    Axios.post(loggerApiURL, {
      message: `Error while clearing cache for consumerId ${consumerID}: ${JSON.stringify(err)}`,
    });
    return null;
  }
};

/**
 * function return the active brands
 */

export const filterBrands = (brandsArray: any, searchQueryStateBrands: any) => {
  /**
   * To store the non disable brands in filteredBrandsByCategory
   */
  const filteredBrandsByCategory: any = {};
  brandsArray?.forEach((category: any) => {
    const categoryBrands = category?.brandsLabelListItems;
    categoryBrands?.forEach((brand: any) => {
      const brandTagId = brand?.brandTagId;
      /**
       * Checking the brands in searchQueryState brands and the brand state in the jsonContent
       */
      if (brand?.brandTagId && searchQueryStateBrands?.[brand?.brandTagId] && !brand?.disable) {
        filteredBrandsByCategory[brandTagId] = brandTagId;
      }
    });
  });
  /**
   * Retrun the non disabled brands
   */
  return filteredBrandsByCategory;
};

//function to call the GeoCoder API and return the address details, anyone can extract whatever exactly is required for them from the response.
export const geoCodeAddress = (location: string): Promise<any> => {
  return new Promise((resolve, reject) => {
    if (!window?.google?.maps?.Geocoder) {
      reject(new Error('Geocoder service not available'));
      return;
    }
    const geocoder = new window.google.maps.Geocoder();
    geocoder?.geocode({ address: location }, (results: any, status: any) => {
      if (status === 'OK') {
        if (results && results.length) {
          resolve(results[0]);
        } else {
          reject(new Error('No results found'));
        }
      } else {
        reject(new Error(`Geocoder failed due to: ${status}`));
      }
    });
  });
};

function getTnTId(dataLayer: any) {
  const MI_Visitor = mtGC('MI_Visitor') || dataLayer?.['cookie_mi_visitor'];
  return MI_Visitor;
}

export const getShopTntId = (dataLayer: any) => {
  const mboxCookie = Cookies?.get('mbox') || '';
  const tntIdString: string[] | null = /PC#(.)+#/.exec(mboxCookie);
  const sessionString: string[] | null = /session#(.)+#/.exec(mboxCookie);
  const mboxCluster: string = Cookies?.get('mboxEdgeCluster') || '';
  const tntId = tntIdString && tntIdString[0] && tntIdString[0].split('#')[1];
  const sessionId = sessionString && sessionString[0] && sessionString[0].split('#')[1];
  if (tntId) {
    return tntId;
  } else if (sessionId) {
    //if tntId is not there fallback to sessionId from mbox cookie
    return mboxCluster ? `${sessionId}.${mboxCluster}_0` : sessionId; //if mboxcluster cookie is present add it to sessionId else fallback to sessionId
  } else {
    return getTnTId(dataLayer); //if both tntId and sessionId are not there in mbox cookie fallback to mi_visitor
  }
};

const SPECIAL_RATE_RETRY_WARNING_CODES = [WARN_CODE_1000];
export const LOWEST_REGULAR_RATE_REQUEST_TYPES = [{ type: 'STANDARD', value: '' }];

const hasNonLowestRegularRate = (requestParams: any): boolean => {
  const rateRequestTypes = requestParams?.variables?.search?.options?.rateRequestTypes;
  if (!Array.isArray(rateRequestTypes)) {
    return false;
  }
  return rateRequestTypes.some(rate => {
    const type = (rate?.type ?? '').toString().toUpperCase();
    const value = (rate?.value ?? '').toString();
    return type !== 'STANDARD' || value !== '';
  });
};

const getResponseWarnings = (
  res: any,
  isDestinationSearch?: boolean,
  isFlexibleDateSearch?: boolean,
  isStateOrCountrySearch?: boolean,
  isAirport?: boolean
): Array<{ code?: string; message?: string }> => {
  if (!res?.data) {
    return [];
  }
  const response = extractDataFromDTTResponse(
    res.data.search,
    isDestinationSearch ?? false,
    isFlexibleDateSearch ?? false,
    isStateOrCountrySearch,
    isAirport
  );
  return response?.status?.[0]?.warnings ?? [];
};

export const executeUXLQueries = (UXLqueryPayloadData: any) => {
  const {
    resolvedUrl,
    headersData,
    query,
    datalayerParams,
    sessionIdValue,
    currentLocale,
    apolloClientRef,
    setSearchResultsState,
    params,
    queryCallback,
    isFlexibleDateSearch,
    isDestinationSearch,
    isStateOrCountrySearch,
    isAirport,
    onRetrySpecialRatesReset,
    searchFallbackExistsCustomerCall,
    searchFallbackTypeCustomerCall,
    sessionData,
  } = UXLqueryPayloadData;
  const { getFlexiblePropertyInfo, getDatedPropertyInfo, getKeywordPropertyInfo } = getDTTShopResult(apolloClientRef); // import required api function
  const UXL_FUNCTIONS_MAPPING: Record<string, any> = {
    getFlexiblePropertyInfo,
    getDatedPropertyInfo,
    getKeywordPropertyInfo,
  };
  const queryCallbackMethod: any = UXL_FUNCTIONS_MAPPING[queryCallback];
  const NEXT_PUBLIC_PREFIX =
    process.env['NEXT_PUBLIC_PREFIX'] || useClientEnvVarsStore.getState().envVarsObject['NEXT_PUBLIC_PREFIX'];
  return queryCallbackMethod(params, isDestinationSearch, isStateOrCountrySearch, isAirport).then(async (res: any) => {
    const { data, errors } = res;
    if (errors & data || data) {
      const warningBlocks = getResponseWarnings(
        res,
        isDestinationSearch,
        isFlexibleDateSearch,
        isStateOrCountrySearch,
        isAirport
      );
      const retryWarnings = warningBlocks.filter(warning =>
        SPECIAL_RATE_RETRY_WARNING_CODES.includes(warning?.code ?? '')
      );
      const warnRates1000RelatedWarningBlock = (
        warningBlocks as Array<{ code?: string; devMessage?: string; message?: string }>
      ).find(warning => warning?.code === WARN_CODE_1000);
      if (retryWarnings.length && hasNonLowestRegularRate(params)) {
        onRetrySpecialRatesReset?.();
        const retryParams = {
          ...params,
          variables: {
            ...params?.variables,
            search: {
              ...params?.variables?.search,
              options: {
                ...params?.variables?.search?.options,
                rateRequestTypes: LOWEST_REGULAR_RATE_REQUEST_TYPES,
              },
            },
          },
        };

        const retryRes = await queryCallbackMethod(retryParams, isDestinationSearch, isStateOrCountrySearch, isAirport);
        if (retryRes?.data) {
          await updateSessionForSpecialRates(
            sessionData,
            useClientEnvVarsStore.getState().envVarsObject['SESSION_GET_CALL_URL_CLIENT'],
            NEXT_PUBLIC_PREFIX
          );
          const searchParams = new URLSearchParams(window.location.search);
          searchParams.set('clusterCode', 'none');
          searchParams.delete('corporateCode');
          window.history.replaceState({}, headersData?.referer, decodeURIComponent(`?${searchParams.toString()}`));
          const searchCounterValue = sessionStorage.getItem('updateSearchCounter');
          // Update the window.location.hash with the current search counter
          window.history.replaceState({}, headersData?.referer, `#/${searchCounterValue}/`);

          const retryDataResponse = {
            ...retryRes,
            data: JSON.parse(JSON.stringify(retryRes.data)),
          };

          // Check if retry response itself has WARN-RATES-1000 before adding original warnings
          const retryResponseWarnings = getResponseWarnings(
            retryRes,
            isDestinationSearch,
            isFlexibleDateSearch,
            isStateOrCountrySearch,
            isAirport
          );

          const retryHasWarn1000 = retryResponseWarnings.some(warning => warning?.code === WARN_CODE_1000);
          retryRes.retryCallwithLrr = true;
          retryRes.retryHasWarn1000 = retryHasWarn1000;
          retryRes.warnRates1000RelatedWarningBlock = warnRates1000RelatedWarningBlock;
          const retryResponseData = {
            res: retryRes,
            headersData,
            setSearchResultsState,
            isFlexibleDateSearch,
            isDestinationSearch,
            isStateOrCountrySearch,
            isAirport,
            searchFallbackExistsCustomerCall,
            searchFallbackTypeCustomerCall,
            retryDataResponse,
          };
          return responseHandler(retryResponseData);
        }
      }
      const responseData = {
        res,
        headersData,
        setSearchResultsState,
        isFlexibleDateSearch,
        isDestinationSearch,
        isStateOrCountrySearch,
        isAirport,
        searchFallbackExistsCustomerCall,
        searchFallbackTypeCustomerCall,
      };
      return responseHandler(responseData);
    } else if (errors) {
      if (usePageContextStore.getState().resultsRetried) {
        callDataLayerForSearchFallback(
          resolvedUrl,
          headersData,
          query,
          datalayerParams,
          sessionIdValue,
          currentLocale,
          false
        );
        usePageContextStore.getState().updatePageContextVar('resultsRetried', false);
      }
      updateURLHashCounter(headersData, false);
      throw errors;
    } else {
      usePageContextStore.getState().updatePageContextVar('resultsRetried', false);
      return res;
    }
  });
};
export const responseHandler = (responseData: any) => {
  const {
    res,
    headersData,
    setSearchResultsState,
    isFlexibleDateSearch,
    isDestinationSearch,
    isStateOrCountrySearch,
    isAirport,
    searchFallbackExistsCustomerCall,
    searchFallbackTypeCustomerCall,
  } = responseData;
  const { data, errors } = res;

  setSearchResultsState([NO_RATES_ARE_AVAILABLE], {
    [NO_RATES_ARE_AVAILABLE]: { [NO_RATES_ARE_AVAILABLE]: { noRatesAvailableForAnyProperty: false } },
  });
  setSearchResultsState([TOO_MANY_PROPERTIES], {
    [TOO_MANY_PROPERTIES]: { [TOO_MANY_PROPERTIES]: { tooManyPorpertiesTriggered: false } },
  });
  const response = extractDataFromDTTResponse(
    res.data.search,
    isDestinationSearch ?? false,
    isFlexibleDateSearch ?? false,
    isStateOrCountrySearch,
    isAirport
  );
  // For retry scenarios, extract warnings from the modified retryRes (which has original warnings added)

  const warningCode1001 = warningUtils.findWarningIndex(response, WARN_CODE_1001);
  const warningCode1000 = warningUtils.findWarningIndex(response, WARN_CODE_1000);
  const warningCode1002 = warningUtils.findWarningIndex(response, WARN_SEARCH_1002);
  const hasWarningCode = warningUtils.hasWarningCode(response, searchFallbackWarningCodes);

  const warningChecks: Array<[number, string]> = [
    [warningCode1000, WARN_CODE_1000],
    [warningCode1002, WARN_SEARCH_1002],
  ];

  const newWarningCodes: string[] = warningChecks.filter(([code]) => code > -1).map(([, value]) => value);

  const firstEdgeBasicInfo = isFlexibleDateSearch
    ? response?.edges?.[0]?.node?.basicInformation
    : response?.edges?.[0]?.node?.property?.basicInformation;
  // If search fallback exists then we will add type in warningCodes
  if (searchFallbackExistsCustomerCall === true) {
    newWarningCodes.unshift(searchFallbackTypeCustomerCall); // Add type value at 0th index of array
  }

  if (res?.retryCallwithLrr) {
    newWarningCodes.push(WARN_CODE_1000);
  }

  if (usePageContextStore.getState().resultsRetried) {
    res.isSearchFallback = true;
    newWarningCodes.push(UXL_COMPLETE_FAIL);
    res.searchFallbackType = newWarningCodes.filter(Boolean).join(',');
    usePageContextStore.getState().updatePageContextVar('resultsRetried', false);
  }

  if (data && warningCode1001 > -1) {
    setSearchResultsState([NO_RATES_ARE_AVAILABLE], {
      [NO_RATES_ARE_AVAILABLE]: { [NO_RATES_ARE_AVAILABLE]: { noRatesAvailableForAnyProperty: true } },
    });
    delete res.errors;
    if (res) {
      res.isSearchFallback = true;
    }
    newWarningCodes.push(WARN_CODE_1001);
    res.searchFallbackType = newWarningCodes.filter(Boolean).join(',');
    return res;
  } else if (data && warningCode1000 > -1) {
    if (res?.retryCallwithLrr) {
      res.isSearchFallback = searchFallbackTypeCustomerCall ? searchFallbackTypeCustomerCall : undefined;
      if (searchFallbackTypeCustomerCall) {
        res.searchFallbackType = searchFallbackTypeCustomerCall;
      }
      return res;
    } else {
      res.isSearchFallback = searchFallbackTypeCustomerCall ? searchFallbackTypeCustomerCall : undefined;
      if (searchFallbackTypeCustomerCall) {
        res.searchFallbackType = searchFallbackTypeCustomerCall;
      }
      return res;
    }
  } else if (data && hasWarningCode) {
    const warningCode1009 = warningUtils.findWarningIndex(response, WARN_SEARCH_1009);
    if (warningCode1009 > -1) {
      setSearchResultsState([TOO_MANY_PROPERTIES], {
        [TOO_MANY_PROPERTIES]: { [TOO_MANY_PROPERTIES]: { tooManyPorpertiesTriggered: true } },
      });
      delete res.errors;
      res.isSearchFallback = true;
      newWarningCodes.push(WARN_SEARCH_1009);
      res.searchFallbackType = newWarningCodes.filter(Boolean).join(',');
      return res;
    } else {
      setSearchResultsState([TOO_MANY_PROPERTIES], {
        [TOO_MANY_PROPERTIES]: { [TOO_MANY_PROPERTIES]: { tooManyPorpertiesTriggered: false } },
      });
      res.isSearchFallback = false;
      res.searchFallbackType = newWarningCodes.filter(Boolean).join(',');
      return res;
    }
  } else if (errors && firstEdgeBasicInfo) {
    if (res) {
      res.isSearchFallback = true;
    }
    newWarningCodes.push(UXL_PARTIAL_FAIL);
    res.searchFallbackType = newWarningCodes.filter(Boolean).join(',');
    delete res?.errors;
    return res;
  } else if (data && !errors) {
    if (res) {
      // Only set isSearchFallback when a fallback actually occurs
      if (searchFallbackExistsCustomerCall || res?.retryCallwithLrr) {
        res.isSearchFallback = true;
      }
    }
    // Only set searchFallbackType when there are actual warning codes
    const warningCodesString = newWarningCodes.filter(Boolean).join(',');
    if (warningCodesString) {
      res.searchFallbackType = warningCodesString;
    }
    return res;
  } else {
    updateURLHashCounter(headersData, false);
    throw errors;
  }
};

/* call and update datalayer on error for search fallback */
export async function callDataLayerForSearchFallback(
  resolvedUrl: any,
  headersData: any,
  query: any,
  datalayerParams: any,
  sessionID: string,
  currentLocale?: string,
  isSearchFallback?: boolean,
  searchFallbackType?: string
): Promise<void | unknown> {
  const getPageURI = resolvedUrl ? resolvedUrl.split('?')[0] : '';
  const MOCK_SESSION_ID = '825167AA-9F54-5B1B-95C0-53EEC4E22391';
  const absolutePageURL = `${getPageURI}.mi`;
  const localeKeyValue = currentLocale?.replace('_', '-') ?? 'en-US';
  const channelValue = datalayerParams?.NEXT_PUBLIC_DATALAYER_SITENAME?.split('.')[0];
  const datalayerUrl = datalayerParams?.MI_DATALAYER_APP_URL || '';
  try {
    const getHeaders = (): string => {
      let cookieValues = '';
      if (headersData?.mi_site) {
        cookieValues = cookieValues + ` MI_SITE=${headersData?.mi_site};`;
      }
      if (headersData?.mi_visitor) {
        cookieValues = cookieValues + ` MI_VISITOR=${headersData?.mi_visitor};`;
      }
      if (headersData?.remembermeuserid) {
        cookieValues = cookieValues + ` RememberMeUserID=${encodeURIComponent(headersData?.remembermeuserid)};`;
      }
      if (headersData?.remembermealternateid) {
        cookieValues =
          cookieValues + ` RememberMeAlternateID=${encodeURIComponent(headersData?.remembermealternateid)};`;
      }
      if (headersData?.useridtoken) {
        cookieValues = cookieValues + ` UserIdToken=${encodeURIComponent(headersData?.useridtoken)};`;
      }
      return cookieValues;
    };

    const headersKey: Record<string, string | undefined> = {};

    // creating headers for datalayer call
    if (headersData && Object.keys(headersData).length > 0) {
      DATALAYER_CONSTANT.headerKeys.forEach(name => {
        if (name && headersData[name as KeysRequest['keys']]) {
          headersKey[name] = headersData[name as KeysRequest['keys']];
        }
      });
    }

    let acceptLangHeader;
    if (headersKey['accept-language']) {
      acceptLangHeader = headersKey['accept-language'];
      delete headersKey['accept-language'];
    }
    const reqBody: any = {
      context: {
        localeKey: localeKeyValue,
        programFlag: '',
        siteName: datalayerParams?.NEXT_PUBLIC_DATALAYER_SITENAME,
        mobileAuthEnabled: 'false',
        productSiteId: datalayerParams?.NEXT_PUBLIC_DATALAYER_PRODUCT,
        channel: channelValue,
        pageContent: [],
        pageURI: getPageURI,
        referer: headersData?.referer,
        absolutePageURL: absolutePageURL,
        applicationName: datalayerParams?.NEXT_PUBLIC_DATALAYER_APPLICATION,
        products: datalayerParams?.NEXT_PUBLIC_DATALAYER_PRODUCT,
        template: datalayerParams?.NEXT_PUBLIC_DATALAYER_TEMPLATE,
        seoQueryParams: query,
        isDtt: true,
      },
      sessionToken: sessionID ?? MOCK_SESSION_ID,
      sourceURI: absolutePageURL,
      variation: datalayerParams?.NEXT_PUBLIC_DATALAYER_VARIATION,
    };
    if (isSearchFallback !== undefined) {
      reqBody.context.isSearchFallback = isSearchFallback;
      reqBody.context.searchFallbackType = searchFallbackType;
    }
    // datalayer call
    const dataLayer = await Axios.post(datalayerUrl, reqBody, {
      headers: {
        // please make sure these coookies are in single line. Else there will be `TypeError [ERR_INVALID_CHAR]`
        cookie: `sessionID=${sessionID}; RememberMeFlag=${headersData?.remembermeflag}; ${getHeaders()}`,
        'Accept-Language': getLanguage() || acceptLangHeader || NEXT_PUBLIC_DEFAULT_LANG || '',
        ...headersKey,
      },
    });
    const datalayerResponse = dataLayer?.data?.component?.data?.dataProperties;

    if (datalayerResponse?.length && window?.dataLayer) {
      for (const [key] of Object.entries(window.dataLayer)) {
        datalayerResponse?.forEach((entry: any) => {
          if (window.dataLayer) {
            if (key === entry.key) {
              window.dataLayer[key] = entry.value;
            } else {
              window.dataLayer[entry.key] = entry.value;
            }
          }
        });
      }
    }
    updateURLHashCounter(headersData, true);
    return; // Explicit return for success case
  } catch (error) {
    return error;
  }
}

export const constructAdfOrRlmURL = (
  availabilitySearchURL: string,
  navigationADFURL: string,
  navigationRLMURL: string,
  isFlexibleDateSearch: boolean,
  propertyID: string,
  currentCurrency: string,
  includeTaxesAndFees: boolean
) => {
  return isFlexibleDateSearch
    ? addSubDirectoryPrefix(`${availabilitySearchURL}${navigationADFURL}`) + propertyID + '&isSearch=true&currency='
    : addSubDirectoryPrefix(`${availabilitySearchURL}${navigationRLMURL}`) +
        propertyID +
        '&isSearch=true&currency=' +
        (currentCurrency === 'default' ? '' : currentCurrency) +
        '&showFullPrice=' +
        includeTaxesAndFees +
        (currentCurrency === 'default' ? '' : '&redirectedFromSERP=true');
};

export const isServerError = (errorsToCheck: Array<number>, errorCode: number) => {
  if (errorsToCheck.includes(errorCode)) {
    return true;
  } else {
    return false;
  }
};

export const updateURLHashCounter = (
  headersData: any,
  incrementHashValue: boolean,
  pageType: 'serp' | 'adf' = 'serp'
) => {
  const sessionKey = pageType === 'adf' ? 'updateAdfSearchCounter' : 'updateSearchCounter';
  let localSearchCounterValue;
  if (sessionStorage.getItem(sessionKey)) {
    const sessionValue = sessionStorage.getItem(sessionKey);
    if (incrementHashValue) {
      sessionStorage.setItem(sessionKey, (parseInt(sessionValue as string) + 1).toString());
      localSearchCounterValue = (parseInt(sessionValue as string) + 1).toString();
    } else {
      localSearchCounterValue = sessionValue;
    }
  } else {
    sessionStorage.setItem(sessionKey, '0');
    localSearchCounterValue = '0';
  }
  if (window && window.dataLayer) {
    window.dataLayer['updates_search_call_counter'] = localSearchCounterValue;
  }
  // Update the window.location.hash with the update search counter
  window.history.replaceState({}, headersData?.referer as string, `#/${localSearchCounterValue}/`);
};
